import React, { useState, useEffect, useRef } from 'react';
import { Package, AlertCircle, CheckCircle, RefreshCw, List, Check, ArrowRight, ChevronDown, ChevronRight, Truck, Ship, X, Plus, PackageSearch, Printer, ArrowDownToLine } from 'lucide-react';
import { pOrderLineService } from '../../../../services/pOrderLineService';
import { POrderLineDto, POrdersDto } from '../../../../types/porders';
import { StcTransLineStatus } from '../../../../types/stcTrans';
import { StcTransDto, stcTransService, CreateStcTransRequest, PendingPutAwayItem } from '../../../../services/stcTransService';
import { pOrdersService } from '../../../../services/pOrdersService';
import { bilmottakService } from '../../../../services/bilmottakService';
import { POrderLineBrowser, type LineWithArrived } from './search/POrderLineBrowser';
import { DirectPlacementModal } from './DirectPlacementModal';
import { PutAwayModal } from './PutAwayModal';
import { StcTransEditModal } from './StcTransEditModal';
import { DirectReceiptModal } from './DirectReceiptModal';
import { OverReceiveConfirmModal } from './OverReceiveConfirmModal';
import { userProfileService } from '../../../../services/userProfileService';
import { UserProfileDto } from '../../../../types/userProfile';
import { itemsService } from '../../../../services/itemsService';
import { ItemMasterDataValidationDto } from '../../../../types/advancedItemConfig';
import { itemUnitsService } from '../../../../services/itemUnitsService';
import { BackButton } from '../../../common/BackButton';
import { MediaCaptureButton } from '../../../common/media/MediaCaptureButton';
import { ReceivingActiveLine } from './ReceivingActiveLine';
import { ReceiptExtraInfo } from './ReceiptExtraInfo';
import { ReceiptLineCard } from './ReceiptLineCard';
import { ReceiptActionMenu, type ReceiptActionMenuItem } from './ReceiptActionMenu';
import { FreeReceivingScanPanel } from './FreeReceivingScanPanel';
import { useFreeReceiving, mapHeaderLineToOrderLine, ScannedBatchInfo } from './hooks/useFreeReceiving';
import { POrderLineWithHeaderDto } from '../../../../types/purchaseOrderLines';
import { AdvancedItemConfigModal } from './AdvancedItemConfigModal';
import { SerialNumberReceiptModal } from './modals/SerialNumberReceiptModal';
import { printingService } from '../../../../services/printingService';
import { useSimpleBarcodeScanner } from '../../../../hooks/useSimpleBarcodeScanner';
import { useBarcodeRegistrationEnabled } from '../../../../hooks/useBarcodeRegistrationEnabled';
import { AddBarcodeModal } from './AddBarcodeModal';
import { MobileBarcodeCaptureInput } from '../../../common/scanning';
import { ScanTextInput, NumericInput } from '../../../common/inputs';
import { ReceiptPrintModal } from './modals/ReceiptPrintModal';
import { useBarcodeScannerEnabled } from '../../../../hooks/useBarcodeScannerEnabled';
import { useGtinAsSerialWhenQtyOne } from '../../../../hooks/useGtinAsSerialWhenQtyOne';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { buildStcTransUpdateRequest, updateTransactionWithFallback, processBarcodeInput, resolveReceiptExtraInfoValues } from './helpers';
import { useReceiptExtraInfoFields } from './hooks/useReceiptExtraInfoFields';
import { useWarehouse } from '../../../../contexts/WarehouseContext';
import { stcSortingBoxDefService, StcSortingBoxDefDto } from '../../../../services/stcSortingBoxDefService';
import { CloseSortingBoxModal } from './modals/CloseSortingBoxModal';
import reportConfigurationService, { ReportConfiguration, ReportType } from '../../../../services/reportConfigurationService';

/** Receiving mode determines how the component filters and validates items */
type ReceivingMode = 'order' | 'shipment' | 'bilmottak' | 'free';

interface POrderReceivingDetailProps {
    /** Order number for Modus 1 (PO-based receiving) */
    orderNo?: number;
    /** Shipment number for Modus 3 (Inbound Shipment receiving) */
    inboundShipmentNo?: string;
    /** BatchID for Modus 4 (Bilmottak/Truck receiving) - shared BatchID for multi-operator receiving */
    bilmottakBatchId?: string;
    /** Receiving mode: 'order' = filter by orderNo, 'shipment' = filter by shipmentNo, 'bilmottak' = multi-operator truck receiving */
    receivingMode?: ReceivingMode;
    onBack: () => void;
    onNavigateToPutAway?: (stcOrderNo: number) => void;
}

interface ReceivingLine {
    orderLine: POrderLineDto;
    receivedTransactions: StcTransDto[];
    totalReceived: number;
    remainingQuantity: number;
}

interface BatchEditState {
    [transactionId: string]: {
        itemBatchNo: string;
        itemBatchExpDate: string;
        quantity: number;
        isEditing: boolean;
        isSaving: boolean;
        isEditingQuantity: boolean;
    };
}

export const POrderReceivingDetail: React.FC<POrderReceivingDetailProps> = ({
    orderNo,
    inboundShipmentNo,
    bilmottakBatchId,
    receivingMode = 'order',
    onBack,
    onNavigateToPutAway
}) => {
    const { t } = useTranslation();
    const { currentWarehouse, warehouses } = useWarehouse();
    const extraInfoFields = useReceiptExtraInfoFields();

    // TW 36320099: registrering av ny strekkode ved skann-bom (styrt av items.allowBarcodeRegistration)
    const barcodeRegistrationEnabled = useBarcodeRegistrationEnabled();
    const [showAddBarcodeModal, setShowAddBarcodeModal] = useState(false);
    const [addBarcodeScanData, setAddBarcodeScanData] = useState<{ itemNo?: string; quantity?: number; sscc?: string; batchNo?: string; expiryDate?: string } | null>(null);
    const [order, setOrder] = useState<POrdersDto | null>(null);
    // For shipment mode, we may have multiple orders - track the first one for display
    const [shipmentInfo, setShipmentInfo] = useState<{ shipmentNo: string; supplierNames: string } | null>(null);
    // For bilmottak mode, track session info
    const [bilmottakInfo, setBilmottakInfo] = useState<{ batchId: string; name: string; warehouse: string } | null>(null);
    const [receivingLines, setReceivingLines] = useState<ReceivingLine[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [newReceipt, setNewReceipt] = useState<{ lineNo: number; quantity: number; orderNo?: number; } | null>(null);
    const [batchId, setBatchId] = useState<string>('');
    const [showLineBrowser, setShowLineBrowser] = useState(false);
    const [selectedSearchLine, setSelectedSearchLine] = useState<POrderLineDto | null>(null);
    const [availableOrderLines, setAvailableOrderLines] = useState<POrderLineDto[]>([]);
    const [hasTransactions, setHasTransactions] = useState(false);
    const [canFinishReceiving, setCanFinishReceiving] = useState(false);
    const [isFinishingReceiving, setIsFinishingReceiving] = useState(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [inputJustFocused, setInputJustFocused] = useState(false);
    const [showPutAwayModal, setShowPutAwayModal] = useState(false);
    const [selectedStcTrans, setSelectedStcTrans] = useState<StcTransDto | null>(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDirectReceiptModal, setShowDirectReceiptModal] = useState(false);
    // UX-A — Bulk-modus for serial-mottak. Populeres når handleDirectReceipt finner
    // >= 2 pending serial-linjer for samme item; passeres som prop til DirectReceiptModal.
    const [directReceiptBulkLines, setDirectReceiptBulkLines] = useState<StcTransDto[] | undefined>(undefined);
    // Norlux P19/P20 — "Legg direkte ut på lokasjon": valgte linjer fra ordrelinje-browseren
    const [directPlacementLines, setDirectPlacementLines] = useState<LineWithArrived[] | null>(null);
    const [batchEditState, setBatchEditState] = useState<BatchEditState>({});
    const [showLockedLines, setShowLockedLines] = useState(false);
    const [editingStcTrans, setEditingStcTrans] = useState<StcTransDto | null>(null);
    const [selectedLineItemType, setSelectedLineItemType] = useState<number | undefined>(undefined);
    const [showAdvancedConfigModal, setShowAdvancedConfigModal] = useState(false);
    const [advancedConfigItemNo, setAdvancedConfigItemNo] = useState<string | null>(null);
    const [allowAdvItemConfig, setAllowAdvItemConfig] = useState(false);
    // Master-data validation queue (advanced warehouses only).
    // When operator selects/scans a line whose item lacks BinGrpNo / ItemBinSize,
    // the line(s) are queued and the AdvancedItemConfigModal is shown sequentially
    // for each invalid item before the original receiving action proceeds.
    const [validationQueue, setValidationQueue] = useState<{ itemNo: string; binGrpNoMissing: boolean; itemBinSizeMissing: boolean; }[]>([]);
    const [pendingValidationAction, setPendingValidationAction] = useState<(() => void) | null>(null);
    const [currentValidation, setCurrentValidation] = useState<{ itemNo: string; binGrpNoMissing: boolean; itemBinSizeMissing: boolean; } | null>(null);
    const validationSavedRef = useRef(false);
    // Gjenstående grunnlagsrader (StcIntRelocation LineStatus=20) der plassering-generering
    // feilet etter at batchen ble ferdigstilt (f.eks. «No bins found»). Vises som varselboks
    // med retry-knapp — også når batchen er lukket.
    const [pendingPutAwayItems, setPendingPutAwayItems] = useState<PendingPutAwayItem[]>([]);
    const [isRetryingPutAway, setIsRetryingPutAway] = useState(false);
    const [retryPutAwayMessage, setRetryPutAwayMessage] = useState<string | null>(null);
    const [isBulkProcessing, setIsBulkProcessing] = useState(false);
    const [bulkProcessingMessage, setBulkProcessingMessage] = useState('');
    const [userProfile, setUserProfile] = useState<UserProfileDto | null>(null);
    // Sist brukte ankomstlokasjon i denne mottakssesjonen. Brukes som default
    // for nye linjer slik at operatøren ikke må re-velge lokasjon for hver linje.
    // Faller tilbake til userProfile.defaultGRBinCode når den er null.
    const [lastUsedReceivingLocation, setLastUsedReceivingLocation] = useState<string | null>(null);
    const [warningMessage, setWarningMessage] = useState<string>('');
    const [validationError, setValidationError] = useState<string>('');
    const [validationWarning, setValidationWarning] = useState<string>('');
    const [showOverReceiveConfirmModal, setShowOverReceiveConfirmModal] = useState(false);
    const [pendingOverReceiveAction, setPendingOverReceiveAction] = useState<(() => Promise<void>) | null>(null);
    const [overReceiveModalMessage, setOverReceiveModalMessage] = useState('');
    const [showAddPOModal, setShowAddPOModal] = useState(false);
    const [addPOAvailableOrders, setAddPOAvailableOrders] = useState<POrdersDto[]>([]);
    const [addPOSelectedNos, setAddPOSelectedNos] = useState<Set<number>>(new Set());
    const [addPOLoading, setAddPOLoading] = useState(false);
    const [addPOSearchTerm, setAddPOSearchTerm] = useState('');
    // Serial number modal state (ItemType=6)
    type SerialNumberPendingData = {
        lineNo: number;
        quantity: number;
        batchNo?: string;
        expDate?: string;
        receivingLocation?: string;
        action: 'save' | 'directReceipt';
        itemNo: string;
        itemDescription: string;
    };
    const [showSerialNumberModal, setShowSerialNumberModal] = useState(false);
    const [serialNumberPendingData, setSerialNumberPendingData] = useState<SerialNumberPendingData | null>(null);
    // Innstilling: bruk innskannet varenummer/GTIN direkte som serienummer i Fritt mottak når antall = 1
    const gtinAsSerialWhenQtyOne = useGtinAsSerialWhenQtyOne();

    // Sorting box mode state
    const [sortingModeActive, setSortingModeActive] = useState(false);
    const [activeSortingBoxes, setActiveSortingBoxes] = useState<Map<string, number>>(new Map());
    // Map: stcBoxTypeID -> currentStcBoxNo
    const [sortingBoxDefs, setSortingBoxDefs] = useState<StcSortingBoxDefDto[]>([]);
    const [itemBoxTypeCache, setItemBoxTypeCache] = useState<Map<string, string>>(new Map());
    // Map: itemNo -> stcBoxTypeID (cached from item lookups)
    const [showCloseSortingBoxModal, setShowCloseSortingBoxModal] = useState(false);
    const [closingSortingBoxNo, setClosingSortingBoxNo] = useState<number | null>(null);
    const [closingSortingBoxTypeId, setClosingSortingBoxTypeId] = useState<string>('');

    // Sorting box label templates (Reports med ReportType=70). Hentes når sorteringsmodus aktiveres.
    const [sortingBoxReports, setSortingBoxReports] = useState<ReportConfiguration[]>([]);
    // Modal-state for valg av mal når det finnes flere
    const [showReportPicker, setShowReportPicker] = useState(false);
    const [reportPickerSelection, setReportPickerSelection] = useState<number | null>(null);
    // Promise-resolver brukt av modalen for å returnere valgt reportId til kallende kode
    const reportPickerResolverRef = useRef<((reportId: number | null) => void) | null>(null);

    const quantityInputRef = useRef<HTMLInputElement>(null);
    const scannerRef = useRef<HTMLInputElement>(null);

    // Resolve UseAWYesNo for the warehouse this receipt is bound to.
    // Falls back to currentWarehouse when the order has no explicit warehouse set.
    const isAdvancedWarehouse = (() => {
        const warehouseId = order?.warehouse || currentWarehouse;
        if (!warehouseId) return false;
        return warehouses.find(w => w.warehouseID === warehouseId)?.useAWYesNo ?? false;
    })();

    useEffect(() => {
        loadOrderData();
    }, [orderNo, inboundShipmentNo, bilmottakBatchId, receivingMode]);

    // Load user profile to check advanced config permission
    useEffect(() => {
        const loadUserProfile = async () => {
            try {
                const profile = await userProfileService.getCurrentUserProfile();
                console.log('🔍 User Profile Loaded:', profile);
                console.log('🔍 extraQtyOnGRNotAllowed:', profile.extraQtyOnGRNotAllowed);
                console.log('🔍 extraQtyOnGRConfirm:', profile.extraQtyOnGRConfirm);
                setUserProfile(profile);  // Store full profile
                setAllowAdvItemConfig(profile.allowAdvItemConfigYesNo || false);
            } catch (error) {
                console.error('Error loading user profile:', error);
                setAllowAdvItemConfig(false);
            }
        };
        loadUserProfile();
    }, []);

    // Fetch itemType when selectedSearchLine changes
    useEffect(() => {
        const fetchItemType = async () => {
            if (selectedSearchLine && order) {
                try {
                    // I fritt mottak er order syntetisk med tom companyNo — bruk linjens egen company.
                    const companyNo = selectedSearchLine.companyNo || order.companyNo;
                    const item = await itemsService.getItemByCompanyAndItemNo(
                        companyNo,
                        selectedSearchLine.itemNo
                    );
                    setSelectedLineItemType(item.itemType);
                } catch (error) {
                    console.warn('Could not fetch itemType for selected line:', error);
                    setSelectedLineItemType(undefined);
                }
            } else {
                setSelectedLineItemType(undefined);
            }
        };
        fetchItemType();
    }, [selectedSearchLine, order]);

    // Auto-focus disabled - global scanning is active
    // useEffect(() => {
    //     if (newReceipt && quantityInputRef.current) {
    //         quantityInputRef.current.focus();
    //         const input = quantityInputRef.current;
    //         setTimeout(() => {
    //             const length = input.value.length;
    //             input.setSelectionRange(length, length);
    //         }, 0);
    //     }
    // }, [newReceipt]);

    // Toggle sorting mode on/off
    const handleToggleSortingMode = async () => {
        if (sortingModeActive) {
            // Deactivate sorting mode
            setSortingModeActive(false);
            setActiveSortingBoxes(new Map());
            setItemBoxTypeCache(new Map());
            setSortingBoxDefs([]);
            setSortingBoxReports([]);
            return;
        }

        // Activate sorting mode
        try {
            const warehouse = order?.warehouse || currentWarehouse || 'MAIN';

            // Load sorting box definitions
            const boxDefs = await stcSortingBoxDefService.getCurrent();
            setSortingBoxDefs(boxDefs);

            // Load sorting box label templates (ReportType=70). Filtrer til aktive maler.
            // I fritt mottak er order syntetisk med tom companyNo — fall tilbake til linjenes company.
            const sortingCompanyNo = order?.companyNo || availableOrderLines[0]?.companyNo;
            if (sortingCompanyNo) {
                try {
                    const reports = await reportConfigurationService.getByType(sortingCompanyNo, ReportType.SortingBoxLabel);
                    setSortingBoxReports(reports.filter(r => r.activeYesNo));
                } catch (err) {
                    console.error('Klarte ikke laste sorteringskasse-maler:', err);
                    setSortingBoxReports([]);
                }
            }

            // Build item -> stcBoxTypeID map from available order lines
            const boxTypeMap = new Map<string, string>();
            const distinctBoxTypes: string[] = [];

            for (const line of availableOrderLines) {
                try {
                    const item = await itemsService.getItemByCompanyAndItemNo(
                        line.companyNo || order?.companyNo || '',
                        line.itemNo
                    );
                    if (item.stcBoxTypeID) {
                        boxTypeMap.set(line.itemNo, item.stcBoxTypeID);
                        if (!distinctBoxTypes.includes(item.stcBoxTypeID)) {
                            distinctBoxTypes.push(item.stcBoxTypeID);
                        }
                    }
                } catch (err) {
                    console.warn(`Could not fetch stcBoxTypeID for item ${line.itemNo}:`, err);
                }
            }

            if (distinctBoxTypes.length === 0) {
                setError(t('warehouse.modules.receipt.noSortingBoxTypes'));
                return;
            }

            setItemBoxTypeCache(boxTypeMap);

            // Reserve a StcBoxNo for each distinct box type
            const boxNoMap = new Map<string, number>();
            for (const boxTypeId of distinctBoxTypes) {
                const nextBoxNo = await stcTransService.getNextStcBoxNo(warehouse, boxTypeId);
                boxNoMap.set(boxTypeId, nextBoxNo);
            }

            setActiveSortingBoxes(boxNoMap);
            setSortingModeActive(true);
        } catch (err) {
            console.error('Error activating sorting mode:', err);
            setError(t('warehouse.modules.receipt.detail.activateSortingFailed', {}, 'Kunne ikke aktivere sorteringsmodus'));
        }
    };

    // Velg riktig sorteringskasse-mal (ReportType=70).
    // 0 maler -> kaster feil. 1 mal -> auto-velg. >=2 maler -> åpner dropdown og venter på valg.
    const resolveSortingBoxReportId = async (): Promise<number> => {
        if (sortingBoxReports.length === 0) {
            throw new Error(t('warehouse.modules.receipt.detail.noSortingBoxTemplate', {}, 'Ingen aktiv sorteringskasse-mal funnet (ReportType=70). Opprett en mal i Rapport-konfigurasjon før utskrift.'));
        }
        if (sortingBoxReports.length === 1) {
            return sortingBoxReports[0].reportID;
        }
        // Flere maler -> spør operatør hver gang
        return await new Promise<number>((resolve, reject) => {
            reportPickerResolverRef.current = (selected) => {
                if (selected === null) reject(new Error(t('warehouse.modules.receipt.detail.cancelledNoTemplate', {}, 'Avbrutt — ingen mal valgt')));
                else resolve(selected);
            };
            setReportPickerSelection(sortingBoxReports[0].reportID);
            setShowReportPicker(true);
        });
    };

    // Print sorting label for a single zone/box. Krever en valgt reportId (mal).
    const printSortingBoxLabel = async (stcBoxTypeID: string, stcBoxNo: number, reportId: number) => {
        // I fritt mottak er order syntetisk med tom companyNo — fall tilbake til linjenes company.
        const sortingCompanyNo = order?.companyNo || availableOrderLines[0]?.companyNo;
        if (!sortingCompanyNo) throw new Error(t('warehouse.modules.receipt.detail.missingCompanyNo', {}, 'Mangler companyNo'));
        const boxDef = sortingBoxDefs.find(d => d.stcBoxTypeID === stcBoxTypeID);
        const result = await printingService.printSortingBoxLabel(sortingCompanyNo, {
            stcBoxTypeID,
            description: boxDef?.description1 || stcBoxTypeID,
            stcBoxNo,
            profileId: userProfile?.profileID || undefined,
            reportId,
            numOfCopies: 1
        });
        if (!result.success) {
            throw new Error(result.message || t('warehouse.modules.receipt.detail.printFailed', {}, 'Utskrift feilet'));
        }
        return result;
    };

    // Print sorting labels for all active sorting boxes
    const handlePrintAllSortingLabels = async () => {
        let reportId: number;
        try {
            reportId = await resolveSortingBoxReportId();
        } catch (err: any) {
            setError(err?.message || t('warehouse.modules.receipt.detail.selectTemplateFailed', {}, 'Klarte ikke å velge mal for sorteringskasse'));
            return;
        }
        const errors: string[] = [];
        let printed = 0;
        for (const [stcBoxTypeID, stcBoxNo] of Array.from(activeSortingBoxes.entries())) {
            try {
                await printSortingBoxLabel(stcBoxTypeID, stcBoxNo, reportId);
                printed++;
            } catch (err: any) {
                errors.push(`${stcBoxTypeID}: ${err?.message || t('warehouse.modules.receipt.detail.errorShort', {}, 'feil')}`);
            }
        }
        if (errors.length > 0) {
            setError(t('warehouse.modules.receipt.detail.printFailedWithErrors', { 0: errors.join('; ') }, `Utskrift feilet:\n${errors.join('; ')}`));
        } else if (printed > 0) {
            setWarningMessage(t('warehouse.modules.receipt.detail.sortingLabelsSent', { 0: printed }, `${printed} sorteringslapp(er) sendt til printer`));
            setTimeout(() => setWarningMessage(''), 3000);
        }
    };

    // Get sorting box info for a specific item (used by ReceivingActiveLine)
    const getSortingBoxInfoForItem = (itemNo: string) => {
        if (!sortingModeActive) return undefined;
        const boxTypeId = itemBoxTypeCache.get(itemNo);
        if (!boxTypeId) return undefined;
        const stcBoxNo = activeSortingBoxes.get(boxTypeId);
        if (!stcBoxNo) return undefined;
        const boxDef = sortingBoxDefs.find(d => d.stcBoxTypeID === boxTypeId);
        return {
            stcBoxTypeID: boxTypeId,
            description: boxDef?.description1 || boxTypeId,
            stcBoxNo
        };
    };

    // Handle closing a sorting box
    const handleCloseSortingBox = (stcBoxNo: number) => {
        // Find which box type this is
        activeSortingBoxes.forEach((boxNo, boxTypeId) => {
            if (boxNo === stcBoxNo) {
                setClosingSortingBoxNo(stcBoxNo);
                setClosingSortingBoxTypeId(boxTypeId);
                setShowCloseSortingBoxModal(true);
            }
        });
    };

    // Handle after sorting box is closed
    const handleSortingBoxClosed = async (printNewLabel: boolean) => {
        setShowCloseSortingBoxModal(false);

        // Clear editing state - the closed lines are now locked and shouldn't be shown as editable
        setNewReceipt(null);
        setSelectedSearchLine(null);
        setEditingStcTrans(null);
        setBatchEditState({});
        setScannedBatchInfo({});
        setValidationError('');
        setValidationWarning('');

        if (printNewLabel && closingSortingBoxTypeId) {
            // Reserve a new StcBoxNo for the same zone and print its label
            try {
                const reportId = await resolveSortingBoxReportId();
                const warehouse = order?.warehouse || currentWarehouse || 'MAIN';
                const newBoxNo = await stcTransService.getNextStcBoxNo(warehouse, closingSortingBoxTypeId);
                setActiveSortingBoxes(prev => {
                    const updated = new Map(prev);
                    updated.set(closingSortingBoxTypeId, newBoxNo);
                    return updated;
                });
                await printSortingBoxLabel(closingSortingBoxTypeId, newBoxNo, reportId);
            } catch (err: any) {
                console.error('Error generating/printing new sorting box label:', err);
                setError(err?.message || t('warehouse.modules.receipt.detail.printNewSortingLabelFailed', {}, 'Klarte ikke å skrive ut ny sorteringskasse-etikett'));
            }
        }

        // Reload data to reflect locked lines
        await loadOrderData(true);
        setClosingSortingBoxNo(null);
        setClosingSortingBoxTypeId('');
    };

    const refreshPendingPutAway = async (id: string) => {
        if (!id) {
            setPendingPutAwayItems([]);
            return;
        }
        try {
            const result = await stcTransService.getPendingPutAway(id);
            setPendingPutAwayItems(result.items || []);
        } catch (err) {
            // Ikke-kritisk sjekk — ikke forstyrr mottaksflyten ved feil
            console.error('Error checking pending put-away:', err);
        }
    };

    useEffect(() => {
        setRetryPutAwayMessage(null);
        if (batchId) {
            void refreshPendingPutAway(batchId);
        } else {
            setPendingPutAwayItems([]);
        }
    }, [batchId]);

    const handleRetryPutAway = async () => {
        if (!batchId) return;

        try {
            setIsRetryingPutAway(true);
            setRetryPutAwayMessage(null);
            setError(null);

            const result = await stcTransService.retryPutAway(batchId);

            if (result.remainingCount === 0) {
                setRetryPutAwayMessage(t('warehouse.modules.receipt.detail.retryPutAwaySuccess', { 0: String(result.resolvedCount) }, `Plassering generert for ${result.resolvedCount} vare(r). Varene ligger nå klare i plasseringsmodulen.`));
            } else {
                const failing = result.stillFailingItems
                    .map(f => f.itemText ? `${f.itemNo} (${f.itemText})` : f.itemNo)
                    .join(', ');
                setRetryPutAwayMessage(result.resolvedCount > 0
                    ? t('warehouse.modules.receipt.detail.retryPutAwayPartial', { 0: String(result.resolvedCount), 1: failing }, `Plassering generert for ${result.resolvedCount} vare(r), men feiler fortsatt for: ${failing}. Kontroller lokasjonskonfigurasjonen og prøv igjen.`)
                    : t('warehouse.modules.receipt.detail.retryPutAwayFailed', { 0: failing }, `Plassering feiler fortsatt for: ${failing}. Kontroller lokasjonskonfigurasjonen (BinGrp/ItemBinSize og ledige lokasjoner) og prøv igjen.`));
            }

            await refreshPendingPutAway(batchId);
        } catch (error) {
            console.error('Error retrying put-away generation:', error);
            setError(t('warehouse.modules.receipt.errors.operationFailedWithError', { error: error instanceof Error ? error.message : t('common.errors.unknownError', {}, 'Ukjent feil') }, `Kunne ikke fullføre operasjonen: ${error instanceof Error ? error.message : 'Ukjent feil'}`));
        } finally {
            setIsRetryingPutAway(false);
        }
    };

    const checkCanFinishReceiving = async (batchId: string) => {
        try {
            console.log('Checking if receiving can be finished for batch:', batchId);
            const result = await stcTransService.canFinishReceiving(batchId);
            console.log('Can finish receiving result:', result);
            setCanFinishReceiving(result.canFinish);
            if (!result.canFinish) {
                console.log('Cannot finish receiving:', result.message);
            }
        } catch (error) {
            console.error('Error checking if receiving can be finished:', error);
            setCanFinishReceiving(false);
        }
    };

    const handleFinishReceiving = async () => {
        if (!batchId) return;

        try {
            setIsFinishingReceiving(true);
            await stcTransService.finishReceiving(batchId);

            setShowSuccessMessage(true);
            setError(null);

            setTimeout(() => {
                onBack();
            }, 2000);

        } catch (error) {
            console.error('Error finishing receiving:', error);
            setError(t('warehouse.modules.receipt.errors.finishReceivingFailed', { error: error instanceof Error ? error.message : t('common.errors.unknownError', {}, 'Ukjent feil') }, `Kunne ikke avslutte mottak: ${error instanceof Error ? error.message : 'Ukjent feil'}`));
        } finally {
            setIsFinishingReceiving(false);
        }
    };

    const handleFinishReceivingAndPutAway = async () => {
        if (!batchId || !order) return;

        try {
            setIsFinishingReceiving(true);
            setError(null);

            // Call the endpoint to finish receiving and generate put-away tasks
            // Backend performs bulk ItemBinSize validation before processing
            //
            // Forsendelsesmottak (Modus 3) kan spenne over flere innkjøpsordrer, men `order` her
            // er en SYNTETISK ordre bygget fra første linje. Sender vi den ene orderNo-en, filtrerer
            // backend put-away på kun den ordren (StcIntRelocation.OrderNo = X) → linjer fra de andre
            // ordrene blir mottatt, men får ingen put-away-oppgave. Ved å sende orderNo=0 tar backend
            // samme gren som Åpent mottak (Modus 2): henter ALLE ordrer fra batchen og filtrerer på
            // disse (OrderNo IN (...)). Enkelt-ordre (Modus 1) sender fortsatt sin reelle orderNo
            // (uendret), og Åpent mottak sender allerede 0 → ingen atferdsendring for dem.
            const result = await stcTransService.finishReceivingAndPutAway(batchId, {
                orderNo: receivingMode === 'shipment' ? 0 : order.orderNo
            });

            // Backend bulk pre-validation: items missing ItemBinSize configuration
            if (result.itemsMissingBinConfig && result.itemsMissingBinConfig.length > 0) {
                setError(t('warehouse.modules.receipt.detail.itemsMissingBinConfig', { 0: result.itemsMissingBinConfig.join(', ') }, `Følgende varer mangler bin-konfigurasjon og kan ikke plasseres automatisk: ${result.itemsMissingBinConfig.join(', ')}. Gå til Administrasjon > Put-Away Diagnostikk for detaljer, eller bruk "Avslutt mottak" for å motta uten automatisk plassering.`));
                setIsFinishingReceiving(false);
                return;
            }

            // Backend dry-run pre-flight: bin-oppløsningen (samme kode som put-away-regelen)
            // fant ingen lokasjoner for én eller flere varer. Ingenting er ferdigstilt.
            // Åpne grunndata-modalen for varene (samme kø-mekanisme som mottaksvalidering)
            // slik at finish re-forsøkes automatisk etter at alle er rettet.
            if (result.putAwayBlocked && result.putAwayBlockedItems && result.putAwayBlockedItems.length > 0) {
                const blockedItems = result.putAwayBlockedItems;
                const itemList = blockedItems
                    .map(b => b.itemText ? `${b.itemNo} (${b.itemText})` : b.itemNo)
                    .join(', ');
                setError(t('warehouse.modules.receipt.detail.putAwayBlockedItems', { 0: itemList }, `Plassering kan ikke genereres for: ${itemList}. Mottaket er IKKE avsluttet. Rett opp lokasjonskonfigurasjonen for varene og prøv igjen.`));

                if (isAdvancedWarehouse) {
                    const queue = blockedItems.map(b => ({
                        itemNo: b.itemNo,
                        binGrpNoMissing: false,
                        itemBinSizeMissing: false,
                    }));
                    setValidationQueue(queue);
                    setPendingValidationAction(() => () => { void handleFinishReceivingAndPutAway(); });
                    setCurrentValidation(queue[0]);
                    setAdvancedConfigItemNo(queue[0].itemNo);
                    setShowAdvancedConfigModal(true);
                }

                setIsFinishingReceiving(false);
                return;
            }

            if (result.success) {
                setShowSuccessMessage(true);

                // Show messages from backend
                if (result.messages && result.messages.length > 0) {
                    console.log('Process messages:', result.messages);
                }

                // Navigate to put-away module after a short delay
                setTimeout(() => {
                    if (onNavigateToPutAway && result.stcOrderNo) {
                        onNavigateToPutAway(result.stcOrderNo);
                    } else if (onNavigateToPutAway) {
                        // If no stcOrderNo but function exists, call without parameter (will show list)
                        console.warn('No StcOrderNo received from backend, navigating to put-away list');
                        onNavigateToPutAway(0);
                    } else {
                        // Fallback to going back if navigation function not provided
                        onBack();
                    }
                }, 1500);
            } else {
                // Handle partial success or failure with improved error messages
                if (result.receivingFinished && !result.putAwayTasksGenerated) {
                    // Mottaket er ferdigstilt men plassering feilet — oppdater varselboksen
                    // med gjenstående grunnlagsrader slik at retry-knappen vises umiddelbart.
                    void refreshPendingPutAway(batchId);
                    // Parse messages for item-specific errors
                    const itemErrors = result.messages
                        ?.filter(m => m.includes('No bins found for item') || m.includes('Failed to create put-away reservations'))
                        .map(m => {
                            const match = m.match(/item[:\s]+(\S+)/i);
                            return match ? match[1] : null;
                        })
                        .filter(Boolean) || [];

                    if (itemErrors.length > 0) {
                        setError(t('warehouse.modules.receipt.detail.putAwayFailedForItems', { 0: itemErrors.join(', ') }, `Mottak fullført, men plassering feilet for: ${itemErrors.join(', ')}. Disse varene mangler bin-konfigurasjon (ItemBinSize). Gå til Administrasjon > Put-Away Diagnostikk for detaljer.`));
                    } else {
                        setError(t('warehouse.modules.receipt.detail.putAwayTasksNotCreatedDiagnostics', {}, 'Mottak avsluttet, men plasseringsoppgaver ble ikke opprettet. Se Administrasjon > Put-Away Diagnostikk for detaljer.'));
                    }
                } else {
                    setError(result.errorMessage || t('warehouse.modules.receipt.errors.operationFailed', {}, 'Kunne ikke fullføre operasjonen.'));
                }
            }

        } catch (error) {
            console.error('Error finishing receiving and generating put-away:', error);
            setError(t('warehouse.modules.receipt.errors.operationFailedWithError', { error: error instanceof Error ? error.message : t('common.errors.unknownError', {}, 'Ukjent feil') }, `Kunne ikke fullføre operasjonen: ${error instanceof Error ? error.message : 'Ukjent feil'}`));
        } finally {
            setIsFinishingReceiving(false);
        }
    };

    const handleOpenAdvancedConfig = (itemNo: string) => {
        setAdvancedConfigItemNo(itemNo);
        setShowAdvancedConfigModal(true);
    };

    const handleCloseAdvancedConfig = () => {
        setShowAdvancedConfigModal(false);
        setAdvancedConfigItemNo(null);

        // Queue mode: distinguish save-driven close from operator cancel
        if (validationSavedRef.current) {
            validationSavedRef.current = false;
            const remaining = validationQueue.slice(1);
            setValidationQueue(remaining);
            if (remaining.length > 0) {
                // Open modal for next invalid item after a brief delay so the
                // close animation can settle before the new content appears.
                const next = remaining[0];
                setCurrentValidation(next);
                setTimeout(() => {
                    setAdvancedConfigItemNo(next.itemNo);
                    setShowAdvancedConfigModal(true);
                }, 50);
            } else {
                // Queue empty — run the original action that triggered validation
                const action = pendingValidationAction;
                setCurrentValidation(null);
                setPendingValidationAction(null);
                if (action) action();
            }
        } else if (validationQueue.length > 0) {
            // Operator cancelled the queue — abort the pending receive action
            setValidationQueue([]);
            setCurrentValidation(null);
            setPendingValidationAction(null);
        }
    };

    const handleAdvancedConfigSaved = () => {
        validationSavedRef.current = true;
        // Refresh order data after saving config
        loadOrderData();
    };

    /**
     * Bulk-register all selected lines with their ordered quantities.
     * Extracted from the inline `onAllLinesSelect` handler so the master-data
     * validation step can wrap it in a single continuation.
     */
    const startBulkRegistration = (lines: POrderLineDto[]): void => {
        if (!order) return;

        // Bulk skal registrere GJENSTÅENDE antall (bestilt − levert − allerede ankommet),
        // ikke bestilt antall rått. Linjer uten rest hoppes over — ellers dobbelregistreres
        // linjer som allerede er helt/delvis mottatt (bulk går utenom overmottak-valideringen).
        const linesWithRemaining = lines
            .map(line => ({ line, remaining: calculateRemainingQuantity(line, line.lineNo) }))
            .filter(x => x.remaining > 0);
        const skippedCount = lines.length - linesWithRemaining.length;

        if (linesWithRemaining.length === 0) {
            setError(t('warehouse.modules.receipt.messages.noLinesWithRemaining', {}, 'Ingen linjer har gjenstående antall å motta — alle er allerede mottatt eller ankommet.'));
            return;
        }

        setIsBulkProcessing(true);
        setBulkProcessingMessage(t('warehouse.modules.receipt.messages.registeringLines', { count: linesWithRemaining.length }, `Registrerer ${linesWithRemaining.length} linjer automatisk...`));
        setError(null);

        setTimeout(async () => {
            try {
                let locationName = '';
                try {
                    const profile = await userProfileService.getCurrentUserProfile();
                    locationName = profile.defaultGRBinCode || '';
                } catch (profileError) {
                    console.warn('Kunne ikke hente profil for LocationName:', profileError);
                }

                if (order) {
                    await stcTransService.ensureTransOrderExists(batchId, order);
                }

                const transactions: CreateStcTransRequest[] = linesWithRemaining
                    .map(({ line, remaining }) => {
                        const quantityToReceive = remaining;
                        // Bruk linjens EGEN ERP-ordrenr (kort, f.eks. 5560379), ikke order.orderNoERP.
                        // I forsendelsesmodus er order.orderNoERP satt til hele InboundShipmentNo
                        // («DSV SSZX… 20'», 32 tegn) → overskrider StcTrans.OrderNoERP nvarchar(20)
                        // og gir «String or binary data would be truncated». Per-linje-verdien er også
                        // korrekt for fler-ordre-forsendelser. (Enkeltordre: identisk verdi, uendret.)
                        const lineErpOrderNo = line.orderNoERP || order.orderNoERP;
                        const erpLinkName = lineErpOrderNo + '-' + line.lineNoERP;

                        return {
                            batchID: batchId,
                            lineType: 0,
                            orderNo: line.orderNo,
                            lineNo: line.lineNo,
                            locationName: locationName,
                            quantity: quantityToReceive,
                            transDt: new Date(),
                            warehouse: line.warehouse || order.warehouse || 'MAIN',
                            toWarehouse: line.warehouse || order.warehouse,
                            itemNo: line.itemNo,
                            itemBarCode: line.itemBarCode,
                            description1: line.description1,
                            itemUnitCode: line.itemUnitCode,
                            itemUnitTxt: line.itemUnitTxt,
                            transType: 0,
                            orderNoERP: lineErpOrderNo,
                            lineNoERP: line.lineNoERP,
                            erpLinkName: erpLinkName,
                            itemBatchNo: '',
                            itemBatchExpDate: undefined,
                            lineStatus: StcTransLineStatus.UnderArbeid
                        };
                    });

                await stcTransService.createTransactionsBulk(batchId, transactions);

                setBulkProcessingMessage(skippedCount > 0
                    ? t('warehouse.modules.receipt.messages.linesRegisteredWithSkipped', { count: transactions.length, skipped: skippedCount }, `${transactions.length} linjer registrert! (${skippedCount} hoppet over — ingen rest å motta)`)
                    : t('warehouse.modules.receipt.messages.linesRegistered', { count: transactions.length }, `${transactions.length} linjer registrert!`));

                await loadOrderData();

                if (batchId) {
                    await checkCanFinishReceiving(batchId);
                }

                setTimeout(() => {
                    setIsBulkProcessing(false);
                }, 500);
            } catch (error) {
                console.error('Feil ved bulk-registrering:', error);
                setError(t('warehouse.modules.receipt.errors.bulkRegistrationFailed', {}, 'Feil ved registrering av linjer. Se konsollen for detaljer.'));
                setIsBulkProcessing(false);
            }
        }, 100);
    };

    /**
     * Pre-flight validation for receiving in advanced warehouses (UseAWYesNo = 1).
     * Checks each itemNo for required master data (BinGrpNo + ItemBinSizeGroupID/ItemBinSize).
     * If any are invalid, queues them and opens AdvancedItemConfigModal sequentially.
     * `onAllValid` runs immediately when no validation is needed (standard warehouse,
     * or all items already valid), or after the operator has resolved the entire queue.
     */
    const runMasterDataValidation = async (itemNos: string[], onAllValid: () => void, companyNoOverride?: string): Promise<void> => {
        if (!isAdvancedWarehouse || !order) {
            onAllValid();
            return;
        }
        try {
            // I fritt mottak er order syntetisk med tom companyNo — bruk linjens egen company.
            const companyNo = companyNoOverride || order.companyNo;
            const unique = Array.from(new Set(itemNos.filter(Boolean)));
            const results = await Promise.all(
                unique.map(itemNo =>
                    itemsService.validateMasterDataForReceiving(companyNo, itemNo)
                )
            );
            const invalid = results.filter(
                (r): r is ItemMasterDataValidationDto => r !== null && !r.isValid
            );
            if (invalid.length === 0) {
                onAllValid();
                return;
            }
            const queue = invalid.map(r => ({
                itemNo: r.itemNo,
                binGrpNoMissing: r.binGrpNoMissing,
                itemBinSizeMissing: r.itemBinSizeMissing,
            }));
            setValidationQueue(queue);
            setPendingValidationAction(() => onAllValid);
            setCurrentValidation(queue[0]);
            setAdvancedConfigItemNo(queue[0].itemNo);
            setShowAdvancedConfigModal(true);
        } catch (err) {
            console.error('Master data validation failed:', err);
            // On validation failure, allow flow to proceed rather than blocking the operator.
            onAllValid();
        }
    };

    const loadOrderData = async (bustCache: boolean = false): Promise<ReceivingLine[] | undefined> => {
        try {
            setLoading(true);
            setError(null);

            let orderLines: POrderLineDto[];
            let usedBatchId: string;

            if (receivingMode === 'shipment' && inboundShipmentNo) {
                // Modus 3: Inbound Shipment - load lines by shipment number
                console.log('Henter forsendelseslinjer for shipmentNo:', inboundShipmentNo, bustCache ? '(cache-busting enabled)' : '');

                // Get all PO lines for this shipment (may span multiple orders).
                // includeCompleted=true: behold ferdigmottatte linjer i visningen, ellers
                // forsvinner en linje straks den er fullt mottatt (Quantity=QuantityDelivered),
                // og re-åpning av forsendelsen viser «Ingen mottatte varer ennå». Speiler bilmottak.
                const shipmentLines = await pOrderLineService.getShipmentLines(inboundShipmentNo, true);

                if (!shipmentLines || shipmentLines.length === 0) {
                    console.warn('Ingen linjer funnet for forsendelse', inboundShipmentNo);
                    setReceivingLines([]);
                    setShipmentInfo({ shipmentNo: inboundShipmentNo, supplierNames: '' });
                    setLoading(false);
                    return;
                }

                // Convert POrderLineWithHeaderDto to POrderLineDto format
                orderLines = shipmentLines.map(line => ({
                    id: line.id,
                    companyNo: line.companyNo,
                    orderNo: line.orderNo,
                    lineNo: line.lineNo,
                    lineNoERP: line.lineNoERP,
                    lineStatus: line.lineStatus,
                    itemNo: line.itemNo,
                    itemBarCode: line.itemBarCode,
                    description1: line.description1,
                    itemGroup1: line.itemGroup1,
                    itemGroup2: line.itemGroup2,
                    itemGroup3: line.itemGroup3,
                    quantity: line.quantity,
                    quantityDelivered: line.quantityDelivered,
                    itemUnitCode: line.itemUnitCode,
                    itemUnitTxt: line.itemUnitTxt,
                    price: line.price,
                    discount: line.discount,
                    deliveryDate: line.deliveryDate,
                    serialNoItemYesNo: line.serialNoItemYesNo,
                    stdLocationName: line.stdLocationName,
                    warehouse: line.warehouse,
                    orderNoERP: line.orderNoERP,
                    itemBatchNo: line.itemBatchNo,
                    batchItemYesNo: line.batchItemYesNo,
                    orderLineFreeField1: line.orderLineFreeField1,
                    orderLineFreeField2: line.orderLineFreeField2,
                    orderLineFreeField3: line.orderLineFreeField3,
                    inboundShipmentNo: line.inboundShipmentNo,
                    statusText: line.lineStatusText,
                    remainingQuantity: line.remainingQuantity
                } as POrderLineDto));

                // Get unique supplier names from the lines
                const uniqueSuppliers = Array.from(new Set(shipmentLines.map(l => l.supplierName).filter(Boolean)));
                setShipmentInfo({
                    shipmentNo: inboundShipmentNo,
                    supplierNames: uniqueSuppliers.join(', ')
                });

                // For shipment mode, create a batch ID based on shipment number
                // Use first order's batchId pattern but include shipment reference
                const firstOrderNo = shipmentLines[0].orderNo;
                usedBatchId = await stcTransService.getOrCreateBatchId(firstOrderNo);
                console.log('Using batchId for shipment receiving:', usedBatchId);

                // Set a synthetic order for display purposes (use first line's order info)
                const firstLine = shipmentLines[0];
                setOrder({
                    id: 0,
                    companyNo: firstLine.companyNo,
                    orderNo: firstLine.orderNo,
                    orderNoERP: inboundShipmentNo, // Display shipment no in header
                    orderStatus: firstLine.orderStatus,
                    supplierNo: '',
                    supplierName: uniqueSuppliers.join(', '),
                    statusText: 'Forsendelse',
                    // Bruk operatørens AKTIVE lager (der GR-binnen fysisk ligger), ikke linjens
                    // Warehouse-felt. Linje-Warehouse er ERP-metadata og kan avvike (sett i én sak
                    // til '6' mens header/bins er lager 1) — det ga «Bin '600010' finnes ikke i
                    // warehouse 6» ved mottak. Speiler Åpent mottak (Modus 2), som alt bruker currentWarehouse.
                    warehouse: currentWarehouse || firstLine.warehouse
                } as POrdersDto);

            } else if (receivingMode === 'bilmottak' && bilmottakBatchId) {
                // Modus 4: Bilmottak - multi-operator truck receiving with shared BatchID
                // Loads lines via InboundShipmentNo = batchId (Modus 3-style browsing)
                console.log('Bilmottak modus - loading linked PO lines for BatchID:', bilmottakBatchId);

                // Set the BatchID directly - it was already created when session was started
                usedBatchId = bilmottakBatchId;

                // Load linked PO lines (InboundShipmentNo = batchId), including completed lines
                const shipmentLines = await pOrderLineService.getShipmentLines(bilmottakBatchId, true);

                if (shipmentLines && shipmentLines.length > 0) {
                    // Convert POrderLineWithHeaderDto to POrderLineDto format (same as Modus 3)
                    orderLines = shipmentLines.map(line => ({
                        id: line.id,
                        companyNo: line.companyNo,
                        orderNo: line.orderNo,
                        lineNo: line.lineNo,
                        lineNoERP: line.lineNoERP,
                        lineStatus: line.lineStatus,
                        itemNo: line.itemNo,
                        itemBarCode: line.itemBarCode,
                        description1: line.description1,
                        itemGroup1: line.itemGroup1,
                        itemGroup2: line.itemGroup2,
                        itemGroup3: line.itemGroup3,
                        quantity: line.quantity,
                        quantityDelivered: line.quantityDelivered,
                        itemUnitCode: line.itemUnitCode,
                        itemUnitTxt: line.itemUnitTxt,
                        price: line.price,
                        discount: line.discount,
                        deliveryDate: line.deliveryDate,
                        serialNoItemYesNo: line.serialNoItemYesNo,
                        stdLocationName: line.stdLocationName,
                        warehouse: line.warehouse,
                        orderNoERP: line.orderNoERP,
                        itemBatchNo: line.itemBatchNo,
                        batchItemYesNo: line.batchItemYesNo,
                        orderLineFreeField1: line.orderLineFreeField1,
                        orderLineFreeField2: line.orderLineFreeField2,
                        orderLineFreeField3: line.orderLineFreeField3,
                        inboundShipmentNo: line.inboundShipmentNo,
                        statusText: line.lineStatusText,
                        remainingQuantity: line.remainingQuantity
                    } as POrderLineDto));

                    const uniqueSuppliers = Array.from(new Set(shipmentLines.map(l => l.supplierName).filter(Boolean)));
                    const firstLine = shipmentLines[0];

                    setBilmottakInfo({
                        batchId: bilmottakBatchId,
                        name: 'Bilmottak',
                        warehouse: firstLine.warehouse || ''
                    });

                    // Set synthetic order for UI compatibility
                    setOrder({
                        id: 0,
                        companyNo: firstLine.companyNo,
                        orderNo: firstLine.orderNo,
                        orderNoERP: bilmottakBatchId,
                        orderStatus: 20,
                        supplierNo: '',
                        supplierName: uniqueSuppliers.join(', ') || 'Bilmottak',
                        statusText: 'Under arbeid',
                        warehouse: firstLine.warehouse || ''
                    } as POrdersDto);
                } else {
                    // No linked POs yet - show empty state
                    console.log('Bilmottak has no linked PO lines yet');
                    orderLines = [];

                    setBilmottakInfo({
                        batchId: bilmottakBatchId,
                        name: 'Bilmottak',
                        warehouse: ''
                    });

                    setOrder({
                        id: 0,
                        companyNo: '',
                        orderNo: 0,
                        orderNoERP: bilmottakBatchId,
                        orderStatus: 20,
                        supplierNo: '',
                        supplierName: 'Bilmottak',
                        statusText: 'Under arbeid',
                        warehouse: ''
                    } as POrdersDto);
                }

            } else if (receivingMode === 'free') {
                // Fritt mottak: skann vilkårlig vare på tvers av åpne innkjøpsordrer.
                // Syntetisk ordre settes så komponenten rendrer (order=null gir "Ordre ikke funnet").
                setOrder({
                    id: 0,
                    companyNo: '',
                    orderNo: 0,
                    orderNoERP: 'Fritt mottak',
                    orderStatus: 20,
                    supplierNo: '',
                    supplierName: '',
                    statusText: 'Fritt mottak',
                    warehouse: currentWarehouse || userProfile?.defaultWarehouse || ''
                } as POrdersDto);

                // Auto-gjenoppta: hent operatørens aktive frie mottak (kun ett per operatør).
                const active = await freeReceiving.resumeActiveBatch();
                if (!active) {
                    // Ingen aktivt mottak — start tomt. BatchID opprettes ved første linjevalg/lagring.
                    setBatchId('');
                    setReceivingLines([]);
                    setAvailableOrderLines([]);
                    setHasTransactions(false);
                    setCanFinishReceiving(false);
                    setLoading(false);
                    return [];
                }
                // Aktivt mottak funnet — bygg virtuelle linjer fra transaksjonene (samme sti som bilmottak).
                usedBatchId = active.batchID;
                orderLines = [];
            } else if (orderNo) {
                // Modus 1: PO-based receiving - existing logic
                console.log('Henting ordre-informasjon for orderNo:', orderNo, bustCache ? '(cache-busting enabled)' : '');
                const orderData = await pOrdersService.getOrderByOrderNo(orderNo);
                setOrder(orderData);

                console.log('Henting ordrelinjer for orderNo:', orderNo);
                try {
                    orderLines = await pOrderLineService.getOrderLines(orderNo);
                } catch (lineError) {
                    console.warn('Kunne ikke hente ordrelinjer direkte, prøver via details:', lineError);
                    try {
                        const orderDetails = await pOrderLineService.getOrderDetails(orderNo);
                        orderLines = orderDetails.lines;
                    } catch (detailsError) {
                        throw new Error(`Kunne ikke hente ordrelinjer: ${lineError instanceof Error ? lineError.message : 'Ukjent feil'}`);
                    }
                }

                if (!orderLines || orderLines.length === 0) {
                    console.warn('Ingen ordrelinjer funnet for ordre', orderNo);
                    setReceivingLines([]);
                    setLoading(false);
                    return;
                }

                usedBatchId = await stcTransService.getOrCreateBatchId(orderNo);
            } else {
                throw new Error('Verken orderNo, inboundShipmentNo eller bilmottakBatchId er spesifisert');
            }
            setBatchId(usedBatchId);

            // For bilmottak/fritt mottak, load existing transactions directly (no order lines preloaded)
            if ((receivingMode === 'bilmottak' || receivingMode === 'free') && orderLines.length === 0) {
                console.log('Bilmottak/fritt mottak: Loading existing transactions from batch', usedBatchId);
                try {
                    const transactions = await stcTransService.getTransactionsByBatch(usedBatchId);
                    console.log(`Bilmottak: Loaded ${transactions.length} existing transactions`);

                    // Group transactions by orderNo/lineNo to create virtual receiving lines
                    const groupedTransactions = new Map<string, StcTransDto[]>();
                    transactions.forEach(trans => {
                        const key = `${trans.orderNo}-${trans.lineNo}`;
                        if (!groupedTransactions.has(key)) {
                            groupedTransactions.set(key, []);
                        }
                        groupedTransactions.get(key)!.push(trans);
                    });

                    // Create receiving lines from grouped transactions
                    const bilmottakLines: ReceivingLine[] = [];
                    groupedTransactions.forEach((groupTransactions, key) => {
                        const firstTrans = groupTransactions[0];
                        const activeTransactions = groupTransactions.filter(t => t.lineStatus < 50);
                        const stcTransReceived = activeTransactions.reduce((sum, t) => sum + (t.quantity || 0), 0);

                        bilmottakLines.push({
                            orderLine: {
                                id: 0,
                                companyNo: firstTrans.companyNo,
                                orderNo: firstTrans.orderNo || 0,
                                lineNo: firstTrans.lineNo || 0,
                                itemNo: firstTrans.itemNo,
                                description1: firstTrans.description1 || '',
                                quantity: firstTrans.quantityOrigin || firstTrans.quantity || 0,
                                quantityDelivered: 0,
                                itemUnitCode: firstTrans.itemUnitCode || '',
                                itemUnitTxt: firstTrans.itemUnitTxt || '',
                                warehouse: firstTrans.warehouse || '',
                                itemBarCode: firstTrans.itemBarCode || '',
                            } as POrderLineDto,
                            receivedTransactions: groupTransactions,
                            totalReceived: stcTransReceived,
                            remainingQuantity: (firstTrans.quantityOrigin || 0) - stcTransReceived
                        });
                    });

                    setReceivingLines(bilmottakLines);
                    setHasTransactions(transactions.length > 0);

                    // Check if we can finish receiving — spør alltid backend når det finnes
                    // transaksjoner. Backend godtar nå også batcher der alt er direktemottatt
                    // (alle linjer >= 40, ingen i status 20), så en klient-side status-20-sjekk
                    // her ville holdt Avslutt-knappen feilaktig deaktivert i den flyten.
                    if (transactions.length > 0) {
                        await checkCanFinishReceiving(usedBatchId);
                    }

                    setLoading(false);
                    return bilmottakLines; // Early return for bilmottak mode
                } catch (err) {
                    console.warn('Could not load bilmottak transactions:', err);
                    setReceivingLines([]);
                    setHasTransactions(false);
                    setLoading(false);
                    return;
                }
            }

            // Last mottatte StcTrans på tvers av ALLE forsendelsens ordrer. En forsendelse
            // (Modus 3) spenner over flere innkjøpsordrer, hver med sin egen RCV_{orderNo}_*-batch,
            // så det holder ikke å lese bare førsteordrens batch — da blir mottak på de andre
            // ordrene usynlige ved re-åpning. Andre moduser bruker den ene batchen som før.
            let allShipmentTransactions: StcTransDto[] = [];
            try {
                if (receivingMode === 'shipment') {
                    const distinctOrderNos = Array.from(new Set(orderLines.map(l => l.orderNo).filter(Boolean)));
                    const batchIds = new Set<string>([usedBatchId]);
                    const orderBatchIds = await Promise.all(
                        distinctOrderNos.map(o => stcTransService.getOrCreateBatchId(o).catch(() => ''))
                    );
                    orderBatchIds.filter(Boolean).forEach(b => batchIds.add(b));
                    const perBatch = await Promise.all(
                        Array.from(batchIds).map(b => stcTransService.getTransactionsByBatch(b, bustCache).catch(() => []))
                    );
                    allShipmentTransactions = perBatch.flat();
                    console.log(`Shipment-modus: lastet ${allShipmentTransactions.length} transaksjoner fra ${batchIds.size} batch(er) for ${distinctOrderNos.length} ordre(r)`);
                } else {
                    allShipmentTransactions = await stcTransService.getTransactionsByBatch(usedBatchId, bustCache);
                }
            } catch (preloadErr) {
                console.warn('Kunne ikke forhåndslaste transaksjoner:', preloadErr);
                allShipmentTransactions = [];
            }

            const receivingLineData: ReceivingLine[] = await Promise.all(
                orderLines.map(async (line) => {
                    try {
                        const transactions = allShipmentTransactions;
                        // Hent ALLE transaksjoner (inkludert låste med status >= 50)
                        // Filter by both orderNo AND lineNo to avoid duplicates in shipment mode (multiple orders can have same lineNo)
                        const lineTransactions = transactions.filter(t => t.orderNo === line.orderNo && t.lineNo === line.lineNo);
                        console.log(`Order ${line.orderNo} Line ${line.lineNo}: found ${lineTransactions.length} transactions`, lineTransactions.map(t => ({ id: t.id, qty: t.quantity, batch: t.itemBatchNo })));
                        // For beregning av "mottatt" teller vi kun aktive transaksjoner (status < 50)
                        const activeLineTransactions = lineTransactions.filter(t => t.lineStatus < 50);
                        const stcTransReceived = activeLineTransactions.reduce((sum, t) => sum + (t.quantity || 0), 0);
                        
                        // Total mottatt = tidligere ferdigstillte mottak + aktive StcTrans
                        const totalReceived = (line.quantityDelivered || 0) + stcTransReceived;
                        
                        // Gjenværende = bestilt - total mottatt
                        const remainingQuantity = (line.quantity || 0) - totalReceived;

                        return {
                            orderLine: line,
                            receivedTransactions: lineTransactions,
                            totalReceived,
                            remainingQuantity
                        };
                    } catch (err) {
                        console.log('Ingen eksisterende mottakslinjer funnet for batch-ID:', usedBatchId);
                        
                        // Fallback når ingen StcTrans finnes - bruk kun ordrelinje data
                        const totalReceived = line.quantityDelivered || 0;
                        const remainingQuantity = (line.quantity || 0) - totalReceived;
                        
                        return {
                            orderLine: line,
                            receivedTransactions: [],
                            totalReceived,
                            remainingQuantity
                        };
                    }
                })
            );

            setReceivingLines(receivingLineData);
            setAvailableOrderLines(orderLines);

            const totalTransactions = receivingLineData.reduce((sum, line) => sum + line.receivedTransactions.length, 0);
            setHasTransactions(totalTransactions > 0);

            // P19/P20: ikke reåpne en allerede ferdigstilt/eksportert batch. Direkte utlegg lager
            // transaksjoner direkte på LineStatus 50 (ferdig) — da skal vi IKKE kalle startReceiving,
            // som ellers setter batchen tilbake til 20 («under arbeid»). Bare start mottak når det
            // finnes ikke-ferdigstilte transaksjoner (LineStatus < 50).
            const hasUnfinishedTransactions = receivingLineData.some(line =>
                line.receivedTransactions.some(trans => Number(trans.lineStatus) < 50)
            );

            if (totalTransactions > 0) {
                // Start mottak hvis ikke allerede startet — men ikke for ferdigstilte direkte-utlegg-batcher
                if (hasUnfinishedTransactions) {
                    try {
                        await stcTransService.startReceiving(usedBatchId);
                    } catch (startError) {
                        console.log('Could not start receiving (may already be started):', startError);
                    }
                }

                // Aktiver ferdigstill-knappene: spør alltid backend (validerer bins, batch-numre, etc.).
                // Backend godtar nå også batcher der alt er direktemottatt (alle linjer >= 40,
                // ingen i status 20) — en klient-side status-20-gate her ville holdt knappene
                // feilaktig deaktivert i den flyten.
                await checkCanFinishReceiving(usedBatchId);
            } else {
                setCanFinishReceiving(false);
            }

            setLoading(false);
            return receivingLineData;
        } catch (err) {
            console.error('Detaljert feil ved henting av ordre-data:', err);
            setError(t('warehouse.modules.receipt.errors.loadOrderFailed', { error: err instanceof Error ? err.message : t('common.errors.unknownError', {}, 'Ukjent feil') }, `Kunne ikke hente ordre-informasjon: ${err instanceof Error ? err.message : 'Ukjent feil'}`));
            setLoading(false);
            return undefined;
        }
    };

    /**
     * Helper function that performs the actual save operation.
     * Extracted to allow pre-confirmation via modal.
     */
    const proceedWithSave = async (
        lineNo: number,
        quantity: number,
        batchNo?: string,
        expDate?: string,
        receivingLine?: ReceivingLine,
        receivingLocation?: string
    ) => {
        if (!order) return;

        try {
            if (!receivingLine) return;

            // Check if we're editing an existing StcTrans or creating a new one
            if (editingStcTrans) {
                // Update existing StcTrans
                const updateRequest = buildStcTransUpdateRequest(editingStcTrans, {
                    quantity,
                    itemBatchNo: batchNo?.trim() || editingStcTrans.itemBatchNo || undefined,
                    itemBatchExpDate: expDate ? new Date(expDate) : editingStcTrans.itemBatchExpDate,
                    locationName: receivingLocation ?? editingStcTrans.locationName
                });

                await updateTransactionWithFallback(
                    stcTransService,
                    editingStcTrans.batchID,
                    editingStcTrans.lineID,
                    updateRequest
                );

                if (receivingLocation) {
                    setLastUsedReceivingLocation(receivingLocation);
                }
            } else {
                // Create new StcTrans
                await stcTransService.ensureTransOrderExists(batchId, order);

                // Use passed receivingLocation or fall back to profile default
                let locationName = receivingLocation || '';
                if (!locationName) {
                    try {
                        const profile = await userProfileService.getCurrentUserProfile();
                        locationName = profile.defaultGRBinCode || '';
                    } catch (profileError) {
                        console.warn('Kunne ikke hente profil for LocationName:', profileError);
                    }
                }

                let itemType: number | undefined;
                try {
                    // I fritt mottak er order syntetisk med tom companyNo — bruk linjens egen company.
                    const companyNo = receivingLine.orderLine.companyNo || order.companyNo;
                    const item = await itemsService.getItemByCompanyAndItemNo(
                        companyNo,
                        receivingLine.orderLine.itemNo
                    );
                    itemType = item.itemType;
                } catch (itemError) {
                    console.warn('Kunne ikke hente item for ItemType:', itemError);
                }

                // Per-linje ERP-ordrenr (kort) — ikke order.orderNoERP, som i forsendelsesmodus er
                // hele InboundShipmentNo og overskrider StcTrans.OrderNoERP nvarchar(20).
                const erpLinkName = (receivingLine.orderLine.orderNoERP || order.orderNoERP) + '-' + receivingLine.orderLine.lineNoERP;

                const newTransaction: CreateStcTransRequest = {
                    batchID: batchId,
                    lineType: 0,
                    orderNo: receivingLine.orderLine.orderNo, // Use line's orderNo for shipment mode support
                    lineNo: lineNo,
                    locationName: locationName,
                    quantity: quantity,
                    transDt: new Date(),
                    warehouse: receivingLine.orderLine.warehouse || order.warehouse || 'MAIN',
                    lineStatus: StcTransLineStatus.UnderArbeid,
                    itemNo: receivingLine.orderLine.itemNo,
                    itemBarCode: receivingLine.orderLine.itemBarCode,
                    description1: receivingLine.orderLine.description1,
                    itemUnitCode: receivingLine.orderLine.itemUnitCode,
                    itemUnitTxt: receivingLine.orderLine.itemUnitTxt,
                    transType: 0,
                    orderNoERP: receivingLine.orderLine.orderNoERP || order.orderNoERP,
                    lineNoERP: receivingLine.orderLine.lineNoERP,
                    erpLinkName: erpLinkName,
                    itemType: itemType,
                    itemBatchNo: batchNo?.trim() || undefined,
                    itemBatchExpDate: expDate ? new Date(expDate) : undefined,
                    stcBoxNo: sortingModeActive
                        ? activeSortingBoxes.get(itemBoxTypeCache.get(receivingLine?.orderLine.itemNo || '') || '') || undefined
                        : undefined
                };

                console.log('Creating new StcTrans with batch info:', { batchNo, expDate });
                await stcTransService.createTransaction(batchId, newTransaction);

                if (locationName) {
                    setLastUsedReceivingLocation(locationName);
                }
            }

            // Clear all editing states FIRST to prevent stale state conflicts
            console.log('Clearing editing states before reload');
            setNewReceipt(null);
            setSelectedSearchLine(null);
            setEditingStcTrans(null);
            setBatchEditState({}); // Reset batch edit state to force re-initialization from fresh data
            setWarningMessage('');
            setValidationError('');
            setValidationWarning('');
            setError(null);

            // Reload data to refresh the list with updated values
            console.log('Reloading order data after save with cache busting');

            // Add delay to ensure database transaction is committed and events processed
            await new Promise(resolve => setTimeout(resolve, 300));

            // Force cache bust to ensure we get fresh data
            await loadOrderData(true);

            console.log('Order data reloaded, receivingLines should now show updated values');

            // Re-check if receiving can be finished after adding/updating receipt
            if (batchId) {
                await checkCanFinishReceiving(batchId);
            }

        } catch (err) {
            console.error('Error adding/updating receipt:', err);
            setError(t('warehouse.modules.receipt.errors.saveReceiptFailed', { error: err instanceof Error ? err.message : t('common.errors.unknownError', {}, 'Ukjent feil') }, `Kunne ikke lagre mottak: ${err instanceof Error ? err.message : 'Ukjent feil'}`));
        }
    };

    const handleAddReceipt = async (lineNo: number, quantity: number, batchNo?: string, expDate?: string, receivingLocation?: string) => {
        if (!order) return;

        // Validate quantity is positive
        if (quantity <= 0) {
            setError(t('warehouse.modules.receipt.validation.quantityMustBePositive', {}, 'Antall må være større enn 0'));
            return;
        }

        // Validate batch fields (safety check - ReceivingActiveLine should catch this first)
        const itemType = selectedLineItemType;
        if ((itemType === 1 || itemType === 5) && !batchNo?.trim()) {
            setError(t('warehouse.modules.receipt.validation.batchRequired', {}, 'Batchnummer er påkrevd for denne varen'));
            return;
        }
        if (itemType === 4 && !expDate) {
            setError(t('warehouse.modules.receipt.validation.expiryRequired', {}, 'Utløpsdato er påkrevd for denne varen'));
            return;
        }

        // Validate over-receiving - use selectedSearchLine's orderNo for composite key lookup
        const receivingLine = findReceivingLine(lineNo, selectedSearchLine?.orderNo);
        const orderLine = receivingLine?.orderLine;

        if (orderLine) {
            // When editing, get the ORIGINAL transaction quantity from server data (receivedTransactions),
            // not from editingStcTrans which gets updated live as the user types
            const editingQty = editingStcTrans
                ? (receivingLine?.receivedTransactions.find(t => t.lineID === editingStcTrans.lineID)?.quantity || 0)
                : undefined;
            const validation = validateOverReceiving(orderLine, quantity, receivingLine, editingQty);
            console.log('🔍 Validation result:', validation);

            if (!validation.allowed) {
                console.log('🚫 Blocking save - setting error');
                // BLOCKED - Show error inline and return
                setValidationError(validation.message);
                setValidationWarning('');
                return;
            } else if (validation.isWarning) {
                console.log('⚠️ Showing warning modal - waiting for user confirmation');
                // WARNING - Show confirmation modal BEFORE proceeding
                setOverReceiveModalMessage(validation.message);
                setPendingOverReceiveAction(() => async () => {
                    // This function will be called if user clicks "Fortsett"
                    await proceedWithSave(lineNo, quantity, batchNo, expDate, receivingLine, receivingLocation);
                });
                setShowOverReceiveConfirmModal(true);
                return; // Stop here, wait for user decision
            } else {
                console.log('✅ No issues - clearing messages');
                // Clear any previous warnings
                setValidationError('');
                setValidationWarning('');
            }
        }

        // Check if this is a serial number item (ItemType=6) - requires serial number registration
        if (selectedLineItemType === 6 && !editingStcTrans && quantity > 0) {
            const pendingData: SerialNumberPendingData = {
                lineNo,
                quantity,
                batchNo,
                expDate,
                receivingLocation,
                action: 'save',
                itemNo: selectedSearchLine?.itemNo || '',
                itemDescription: selectedSearchLine?.description1 || ''
            };

            // Fritt mottak + innstilling aktiv + antall = 1: bruk innskannet varenummer/GTIN
            // direkte som serienummer — hopp over serienummer-modalen.
            if (gtinAsSerialWhenQtyOne && receivingMode === 'free' && quantity === 1 && scannedBatchInfo.scannedCode) {
                await handleSerialNumberComplete([scannedBatchInfo.scannedCode], pendingData);
                return;
            }

            setSerialNumberPendingData(pendingData);
            setShowSerialNumberModal(true);
            return;
        }

        // If we get here, validation passed without warning - proceed directly
        await proceedWithSave(lineNo, quantity, batchNo, expDate, receivingLine, receivingLocation);
    };

    const handleDeleteReceipt = async (lineID: number, transactionId: number) => {
        try {
            await stcTransService.deleteTransaction(batchId, lineID);
            await loadOrderData();

            // Clear active line state to return to main view
            setNewReceipt(null);
            setSelectedSearchLine(null);
            setEditingStcTrans(null);
            setValidationError('');
            setValidationWarning('');
            setError(null);

            // Re-check if receiving can be finished after deleting receipt
            if (batchId) {
                await checkCanFinishReceiving(batchId);
            }

        } catch (err) {
            console.error('Error deleting receipt:', err);
            setError(t('warehouse.modules.receipt.errors.deleteReceiptFailed', { error: err instanceof Error ? err.message : t('common.errors.unknownError', {}, 'Ukjent feil') }, `Kunne ikke slette mottak: ${err instanceof Error ? err.message : 'Ukjent feil'}`));
        }
    };

    const handlePutAway = (stcTrans: StcTransDto) => {
        setSelectedStcTrans(stcTrans);
        setShowPutAwayModal(true);
    };

    const handlePutAwayAll = () => {
        // For now, we'll use the first available StcTrans as a representative transaction
        // This could be enhanced to handle multiple transactions or create a batch put-away
        const allTransactions = receivingLines.flatMap(line => line.receivedTransactions);
        if (allTransactions.length > 0) {
            setSelectedStcTrans(allTransactions[0]); // Use first transaction as representative
            setShowPutAwayModal(true);
        }
    };

    const handleEditStcTrans = (stcTrans: StcTransDto) => {
        setSelectedStcTrans(stcTrans);
        setShowEditModal(true);
    };

    /**
     * Finner alle pending serial-linjer (ItemType=6 + LineStatus=20) for samme item
     * og batchID som den valgte linjen. Returnerer undefined hvis < 2 linjer
     * (single-line-flyten brukes da i modalen).
     */
    const collectBulkSerialLines = (target: StcTransDto): StcTransDto[] | undefined => {
        if (target.itemType !== 6) return undefined;
        const allTransactions = receivingLines.flatMap(r => r.receivedTransactions);
        const candidates = allTransactions.filter(t =>
            t.itemNo === target.itemNo &&
            t.itemType === 6 &&
            t.lineStatus === 20 &&
            t.batchID === target.batchID
        );
        return candidates.length >= 2 ? candidates : undefined;
    };

    const handleDirectReceipt = (stcTrans: StcTransDto) => {
        setSelectedStcTrans(stcTrans);
        setDirectReceiptBulkLines(collectBulkSerialLines(stcTrans));
        setShowDirectReceiptModal(true);
    };

    /**
     * Helper function that performs the actual direct receipt creation.
     * Extracted to allow pre-confirmation via modal.
     */
    const proceedWithDirectReceipt = async (
        lineNo: number,
        quantity: number,
        batchNo?: string,
        expDate?: string,
        receivingLine?: ReceivingLine,
        receivingLocation?: string
    ) => {
        if (!order) return;

        try {
            if (!receivingLine) {
                setError(t('warehouse.modules.receipt.errors.orderLineNotFound', {}, 'Finner ikke ordrelinje'));
                return;
            }

            // Valider batch-info hvis nødvendig (ItemType 1 eller 4)
            let itemType: number | undefined;
            try {
                // I fritt mottak er order syntetisk med tom companyNo — bruk linjens egen company.
                const companyNo = receivingLine.orderLine.companyNo || order.companyNo;
                const item = await itemsService.getItemByCompanyAndItemNo(
                    companyNo,
                    receivingLine.orderLine.itemNo
                );
                itemType = item.itemType;

                // Valider batch-nummer for ItemType 1/5 (påkrevd), 4 (valgfri - auto-genereres)
                if ((itemType === 1 || itemType === 5) && !batchNo) {
                    setError(t('warehouse.modules.receipt.validation.batchRequired', {}, 'Batchnummer er påkrevd for denne varen'));
                    return;
                }

                // Valider utløpsdato for ItemType 4 (påkrevd)
                if (itemType === 4 && !expDate) {
                    setError(t('warehouse.modules.receipt.validation.expiryRequired', {}, 'Utløpsdato er påkrevd for denne varen'));
                    return;
                }
            } catch (itemError) {
                console.warn('Kunne ikke hente item for ItemType-validering:', itemError);
            }

            // Opprett StcTrans først (samme logikk som handleAddReceipt)
            await stcTransService.ensureTransOrderExists(batchId, order);

            // Use passed receivingLocation or fall back to profile default
            let locationName = receivingLocation || '';
            if (!locationName) {
                try {
                    const profile = await userProfileService.getCurrentUserProfile();
                    locationName = profile.defaultGRBinCode || '';
                } catch (profileError) {
                    console.warn('Kunne ikke hente profil for LocationName:', profileError);
                }
            }

            const erpLinkName = (receivingMode === 'free' ? (receivingLine.orderLine.orderNoERP || order.orderNoERP) : order.orderNoERP) + '-' + receivingLine.orderLine.lineNoERP;

            const newTransaction: CreateStcTransRequest = {
                batchID: batchId,
                lineType: 0,
                orderNo: receivingLine.orderLine.orderNo, // Use line's orderNo for shipment mode support
                lineNo: lineNo,
                locationName: locationName,
                quantity: quantity,
                transDt: new Date(),
                warehouse: receivingLine.orderLine.warehouse || order.warehouse || 'MAIN',
                lineStatus: StcTransLineStatus.UnderArbeid,
                itemNo: receivingLine.orderLine.itemNo,
                itemBarCode: receivingLine.orderLine.itemBarCode,
                description1: receivingLine.orderLine.description1,
                itemUnitCode: receivingLine.orderLine.itemUnitCode,
                itemUnitTxt: receivingLine.orderLine.itemUnitTxt,
                transType: 0,
                orderNoERP: receivingMode === 'free' ? (receivingLine.orderLine.orderNoERP || order.orderNoERP) : order.orderNoERP,
                lineNoERP: receivingLine.orderLine.lineNoERP,
                erpLinkName: erpLinkName,
                itemType: itemType,
                itemBatchNo: batchNo,
                itemBatchExpDate: expDate ? new Date(expDate) : undefined
            };

            console.log('Oppretter StcTrans for direktemottak:', newTransaction);
            const createdStcTrans = await stcTransService.createTransaction(batchId, newTransaction);

            console.log('StcTrans opprettet, åpner direktemottak-modal');

            // Nå åpne direktemottak-modalen med den nyopprettede StcTrans
            setSelectedStcTrans(createdStcTrans);
            setShowDirectReceiptModal(true);

            // Clear aktiv linje state and messages
            setNewReceipt(null);
            setSelectedSearchLine(null);
            setWarningMessage('');
            setValidationError('');
            setValidationWarning('');
            setError(null);

        } catch (err) {
            console.error('Error creating StcTrans for direct receipt:', err);
            setError(t('warehouse.modules.receipt.errors.createReceiptFailed', { error: err instanceof Error ? err.message : t('common.errors.unknownError', {}, 'Ukjent feil') }, `Kunne ikke opprette mottak: ${err instanceof Error ? err.message : 'Ukjent feil'}`));
        }
    };

    /**
     * Håndterer direktemottak fra "Aktiv linje" når vi oppretter NYE mottak.
     * Følger legacy-pattern: Opprett StcTrans først, deretter åpne direktemottak-modal.
     */
    const handleDirectReceiptFromNew = async (lineNo: number, quantity: number, batchNo?: string, expDate?: string, receivingLocation?: string) => {
        if (!order || !newReceipt) return;

        setError(null);

        const receivingLine = findReceivingLine(lineNo, selectedSearchLine?.orderNo);
        if (!receivingLine) {
            setError(t('warehouse.modules.receipt.errors.orderLineNotFound', {}, 'Finner ikke ordrelinje'));
            return;
        }

        // Valider antall
        if (quantity <= 0) {
            setError(t('warehouse.modules.receipt.validation.quantityMustBePositive', {}, 'Antall må være større enn 0'));
            return;
        }

        // Validate over-receiving - get ORIGINAL quantity from server data, not live-updated editingStcTrans
        const editingQty = editingStcTrans
            ? (receivingLine?.receivedTransactions.find(t => t.lineID === editingStcTrans.lineID)?.quantity || 0)
            : undefined;
        const validation = validateOverReceiving(receivingLine.orderLine, quantity, receivingLine, editingQty);
        console.log('🔍 Direct Receipt Validation result:', validation);

        if (!validation.allowed) {
            console.log('🚫 Blocking direct receipt - setting error');
            // BLOCKED - Show error inline and return
            setValidationError(validation.message);
            setValidationWarning('');
            return;
        } else if (validation.isWarning) {
            console.log('⚠️ Showing warning modal for direct receipt - waiting for user confirmation');
            // WARNING - Show confirmation modal BEFORE proceeding
            setOverReceiveModalMessage(validation.message);
            setPendingOverReceiveAction(() => async () => {
                // This function will be called if user clicks "Fortsett"
                await proceedWithDirectReceipt(lineNo, quantity, batchNo, expDate, receivingLine, receivingLocation);
            });
            setShowOverReceiveConfirmModal(true);
            return; // Stop here, wait for user decision
        } else {
            console.log('✅ No issues - clearing messages');
            // Clear any previous warnings
            setValidationError('');
            setValidationWarning('');
        }

        // Check if this is a serial number item (ItemType=6) - requires serial number registration
        if (selectedLineItemType === 6 && quantity > 0) {
            const pendingData: SerialNumberPendingData = {
                lineNo,
                quantity,
                batchNo,
                expDate,
                receivingLocation,
                action: 'directReceipt',
                itemNo: selectedSearchLine?.itemNo || '',
                itemDescription: selectedSearchLine?.description1 || ''
            };

            // Fritt mottak + innstilling aktiv + antall = 1: bruk innskannet varenummer/GTIN
            // direkte som serienummer — hopp over serienummer-modalen og gå rett til Direkte mottak.
            if (gtinAsSerialWhenQtyOne && receivingMode === 'free' && quantity === 1 && scannedBatchInfo.scannedCode) {
                await handleSerialNumberComplete([scannedBatchInfo.scannedCode], pendingData);
                return;
            }

            setSerialNumberPendingData(pendingData);
            setShowSerialNumberModal(true);
            return;
        }

        // If we get here, validation passed without warning - proceed directly
        await proceedWithDirectReceipt(lineNo, quantity, batchNo, expDate, receivingLine, receivingLocation);
    };

    const handleModalClose = async () => {
        setShowPutAwayModal(false);
        setShowEditModal(false);
        setShowDirectReceiptModal(false);
        setSelectedStcTrans(null);
        setDirectReceiptBulkLines(undefined);
        // Refresh data since a StcTrans may have been created before the modal was opened
        await loadOrderData();
    };

    /**
     * When DirectReceiptModal is cancelled, go back to editing the StcTrans
     * that was created before the modal opened — so the user returns to
     * the "Rediger eksisterende mottak" view instead of losing context.
     */
    const handleDirectReceiptClose = async () => {
        const stcTransToReturn = selectedStcTrans;
        setShowDirectReceiptModal(false);
        setSelectedStcTrans(null);
        setDirectReceiptBulkLines(undefined);

        if (stcTransToReturn && stcTransToReturn.lineNo != null) {
            // Refresh data to include the already-created StcTrans, then restore editing context
            const refreshedLines = await loadOrderData();

            // Find the matching orderLine from the freshly loaded data
            const lines = refreshedLines || receivingLines;
            const rl = lines.find((r: ReceivingLine) =>
                r.orderLine.lineNo === stcTransToReturn.lineNo &&
                (stcTransToReturn.orderNo == null || r.orderLine.orderNo === stcTransToReturn.orderNo)
            );
            if (rl) {
                setSelectedSearchLine(rl.orderLine);
                // Find the up-to-date version of the StcTrans from refreshed data
                const freshTrans = rl.receivedTransactions.find(t => t.lineID === stcTransToReturn.lineID);
                const transToRestore = freshTrans || stcTransToReturn;
                setEditingStcTrans(transToRestore);
                // Must also set newReceipt — the ReceivingActiveLine render guard requires both selectedSearchLine AND newReceipt
                setNewReceipt({
                    lineNo: rl.orderLine.lineNo,
                    quantity: transToRestore.quantity || 0,
                    orderNo: rl.orderLine.orderNo
                });
            } else {
                setEditingStcTrans(stcTransToReturn);
            }
        } else {
            await loadOrderData();
        }
    };

    const handleModalSuccess = async () => {
        await loadOrderData();
        handleModalClose();

        // Clear active line state to return to main view
        setNewReceipt(null);
        setSelectedSearchLine(null);
        setEditingStcTrans(null);
        setValidationError('');
        setValidationWarning('');
        setError(null);

        // Re-check if receiving can be finished after modal operations
        if (batchId) {
            await checkCanFinishReceiving(batchId);
        }

        // Gi skannefeltet fokus igjen så neste vare kan skannes direkte uten ekstra trykk
        if (receivingMode === 'free') {
            setScanFocusTick(tick => tick + 1);
        } else {
            // Liten delay så fokus ikke kjemper med modal-lukkingen
            setTimeout(() => scannerRef.current?.focus(), 150);
        }
    };

    const handleConfirmOverReceive = async () => {
        console.log('✅ User confirmed over-receive');
        setShowOverReceiveConfirmModal(false);

        if (pendingOverReceiveAction) {
            await pendingOverReceiveAction();
            setPendingOverReceiveAction(null);
        }
        setOverReceiveModalMessage('');
    };

    const handleCancelOverReceive = () => {
        console.log('❌ User cancelled over-receive');
        setShowOverReceiveConfirmModal(false);
        setPendingOverReceiveAction(null);
        setOverReceiveModalMessage('');
        // Keep data intact - user can modify quantity and retry
    };

    /**
     * Handles completion of serial number registration (ItemType=6).
     * Creates one StcTrans per serial number, each with quantity=1.
     *
     * pendingOverride: brukes når flyten hopper over serienummer-modalen
     * (Fritt mottak med 'receipt.useGtinAsSerialWhenQtyOne' og antall = 1) —
     * da er serialNumberPendingData-state aldri satt.
     */
    const handleSerialNumberComplete = async (serialNumbers: string[], pendingOverride?: SerialNumberPendingData) => {
        setShowSerialNumberModal(false);

        const pending = pendingOverride ?? serialNumberPendingData;
        if (!pending || !order) {
            setError(t('warehouse.modules.receipt.detail.missingSerialData', {}, 'Mangler data for serienummerregistrering'));
            return;
        }

        const { lineNo, batchNo, expDate, receivingLocation, action } = pending;

        try {
            await stcTransService.ensureTransOrderExists(batchId, order);

            // Determine receiving location
            let locationName = receivingLocation || '';
            if (!locationName) {
                try {
                    const profile = await userProfileService.getCurrentUserProfile();
                    locationName = profile.defaultGRBinCode || '';
                } catch (profileError) {
                    console.warn('Kunne ikke hente profil for LocationName:', profileError);
                }
            }

            const receivingLine = findReceivingLine(lineNo, selectedSearchLine?.orderNo);
            const orderLine = receivingLine?.orderLine;
            if (!orderLine) {
                setError(t('warehouse.modules.receipt.errors.orderLineNotFound', {}, 'Finner ikke ordrelinje'));
                setSerialNumberPendingData(null);
                return;
            }

            // Per-linje ERP-ordrenr (kort) — ikke order.orderNoERP, som i forsendelsesmodus er
            // hele InboundShipmentNo og overskrider StcTrans.OrderNoERP nvarchar(20).
            const erpLinkName = (orderLine.orderNoERP || order.orderNoERP) + '-' + orderLine.lineNoERP;

            // Create one StcTrans per serial number, each with quantity=1
            const transactions: CreateStcTransRequest[] = serialNumbers.map(serialNo => ({
                batchID: batchId,
                lineType: 0,
                orderNo: orderLine.orderNo,
                lineNo: lineNo,
                locationName: locationName,
                quantity: 1,
                transDt: new Date(),
                warehouse: orderLine.warehouse || order.warehouse || 'MAIN',
                lineStatus: StcTransLineStatus.UnderArbeid,
                itemNo: orderLine.itemNo,
                itemBarCode: orderLine.itemBarCode,
                description1: orderLine.description1,
                itemUnitCode: orderLine.itemUnitCode,
                itemUnitTxt: orderLine.itemUnitTxt,
                transType: 0,
                orderNoERP: orderLine.orderNoERP || order.orderNoERP,
                lineNoERP: orderLine.lineNoERP,
                erpLinkName: erpLinkName,
                itemType: 6,
                itemSerialNo: serialNo,
                itemBatchNo: batchNo?.trim() || undefined,
                itemBatchExpDate: expDate ? new Date(expDate) : undefined
            }));

            console.log(`Oppretter ${transactions.length} serienummer-transaksjoner`);
            const createdTransactions = await stcTransService.createTransactionsBulk(batchId, transactions);

            // Clear editing states
            setNewReceipt(null);
            setSelectedSearchLine(null);
            setEditingStcTrans(null);
            setBatchEditState({});
            setWarningMessage('');
            setValidationError('');
            setValidationWarning('');
            setError(null);
            setSerialNumberPendingData(null);
            setScannedBatchInfo({});

            // Reload data
            await new Promise(resolve => setTimeout(resolve, 300));
            await loadOrderData(true);

            // If direct receipt was requested, open DirectReceiptModal for the first created transaction
            if (action === 'directReceipt' && createdTransactions.length > 0) {
                const firstTx = createdTransactions[0];
                setSelectedStcTrans(firstTx);
                // UX-A: hvis serial-mottak (ItemType=6) opprettet flere linjer samtidig
                // (typisk N serials i én skannings-runde), aktiver bulk-modus i modalen.
                const sameItemSerialLines = createdTransactions.filter(t =>
                    t.itemType === 6 &&
                    t.itemNo === firstTx.itemNo &&
                    t.lineStatus === 20
                );
                setDirectReceiptBulkLines(sameItemSerialLines.length >= 2 ? sameItemSerialLines : undefined);
                setShowDirectReceiptModal(true);
            }

            // Re-check finish status
            if (batchId) {
                await checkCanFinishReceiving(batchId);
            }

        } catch (err) {
            console.error('Feil ved opprettelse av serienummer-transaksjoner:', err);
            setError(t('warehouse.modules.receipt.detail.createSerialReceiptFailed', { 0: err instanceof Error ? err.message : t('common.errors.unknownError', {}, 'Ukjent feil') }, `Kunne ikke opprette serienummer-mottak: ${err instanceof Error ? err.message : 'Ukjent feil'}`));
            setSerialNumberPendingData(null);
        }
    };

    const initializeBatchEdit = (trans: StcTransDto) => {
        const transId = `${trans.id}`;
        if (!batchEditState[transId]) {
            setBatchEditState(prev => ({
                ...prev,
                [transId]: {
                    itemBatchNo: trans.itemBatchNo || '',
                    itemBatchExpDate: trans.itemBatchExpDate ? new Date(trans.itemBatchExpDate).toISOString().split('T')[0] : '',
                    quantity: trans.quantity || 0, // Bruk faktisk quantity fra StcTrans
                    isEditing: false,
                    isSaving: false,
                    isEditingQuantity: false
                }
            }));
        }
    };
    
    const handleBatchFieldChange = (trans: StcTransDto, field: 'itemBatchNo' | 'itemBatchExpDate', value: string) => {
        const transId = `${trans.id}`;
        setBatchEditState(prev => ({
            ...prev,
            [transId]: {
                ...prev[transId],
                [field]: value,
                isEditing: true
            }
        }));
    };
    
    const handleQuantityChange = (trans: StcTransDto, value: number) => {
        const transId = `${trans.id}`;
        setBatchEditState(prev => ({
            ...prev,
            [transId]: {
                ...prev[transId],
                quantity: value,
                isEditingQuantity: true
            }
        }));
    };
    
    const handleQuantitySave = async (trans: StcTransDto) => {
        const transId = `${trans.id}`;
        const editState = batchEditState[transId];
        
        if (!editState || !editState.isEditingQuantity) {
            return; // Ingen endringer å lagre
        }
        
        if (editState.quantity <= 0) {
            setError(t('warehouse.modules.receipt.validation.quantityMustBePositive', {}, 'Antall må være større enn 0'));
            return;
        }
        
        try {
            setBatchEditState(prev => ({
                ...prev,
                [transId]: { ...prev[transId], isSaving: true }
            }));
            
            // Use the helper to build update request and handle fallback
            const updateRequest = buildStcTransUpdateRequest(trans, {
                quantity: editState.quantity
            });

            await updateTransactionWithFallback(stcTransService, trans.batchID, trans.lineID, updateRequest);
            
            // Mark as saved
            setBatchEditState(prev => ({
                ...prev,
                [transId]: {
                    ...prev[transId],
                    isEditingQuantity: false,
                    isSaving: false
                }
            }));

            // Reload data to show changes
            await loadOrderData();

            // Re-check if receiving can be finished after updating quantity
            if (batchId) {
                await checkCanFinishReceiving(batchId);
            }

        } catch (err) {
            console.error('Error updating transaction quantity:', err);
            setError(t('warehouse.modules.receipt.errors.updateQuantityFailed', { error: err instanceof Error ? err.message : t('common.errors.unknownError', {}, 'Ukjent feil') }, `Kunne ikke oppdatere antall: ${err instanceof Error ? err.message : 'Ukjent feil'}`));

            setBatchEditState(prev => ({
                ...prev,
                [transId]: { ...prev[transId], isSaving: false }
            }));
        }
    };

    const handleSaveAllChanges = async (trans: StcTransDto) => {
        const transId = `${trans.id}`;
        const editState = batchEditState[transId];
        
        if (!editState || (!editState.isEditing && !editState.isEditingQuantity)) {
            return; // Ingen endringer å lagre
        }
        
        try {
            setBatchEditState(prev => ({
                ...prev,
                [transId]: { ...prev[transId], isSaving: true }
            }));
            
            // Valider påkrevde felt før lagring
            if ((trans.itemType === 1 || trans.itemType === 5) && !editState.itemBatchNo.trim()) {
                setError(t('warehouse.modules.receipt.validation.batchRequiredForItemType', {}, 'Batchnummer er påkrevd for denne varetypen'));
                setBatchEditState(prev => ({
                    ...prev,
                    [transId]: { ...prev[transId], isSaving: false }
                }));
                return;
            }
            
            if (trans.itemType === 4 && !editState.itemBatchExpDate) {
                setError(t('warehouse.modules.receipt.validation.expiryRequiredForItemType', {}, 'Utgått dato er påkrevd for denne varetypen'));
                setBatchEditState(prev => ({
                    ...prev,
                    [transId]: { ...prev[transId], isSaving: false }
                }));
                return;
            }
            
            if (editState.quantity <= 0) {
                setError(t('warehouse.modules.receipt.validation.quantityMustBePositive', {}, 'Antall må være større enn 0'));
                setBatchEditState(prev => ({
                    ...prev,
                    [transId]: { ...prev[transId], isSaving: false }
                }));
                return;
            }
            
            // Use the helper to build update request and handle fallback
            const updateRequest = buildStcTransUpdateRequest(trans, {
                quantity: editState.quantity,
                itemBatchNo: editState.itemBatchNo.trim() || undefined,
                itemBatchExpDate: editState.itemBatchExpDate ? new Date(editState.itemBatchExpDate) : undefined
            });

            await updateTransactionWithFallback(stcTransService, trans.batchID, trans.lineID, updateRequest);
            
            // Mark as saved
            setBatchEditState(prev => ({
                ...prev,
                [transId]: {
                    ...prev[transId],
                    isEditing: false,
                    isEditingQuantity: false,
                    isSaving: false
                }
            }));

            // Reload data to show changes
            await loadOrderData();

            // Re-check if receiving can be finished after saving all changes
            if (batchId) {
                await checkCanFinishReceiving(batchId);
            }

        } catch (err) {
            console.error('Error saving all changes:', err);
            setError(t('warehouse.modules.receipt.errors.saveChangesFailed', { error: err instanceof Error ? err.message : t('common.errors.unknownError', {}, 'Ukjent feil') }, `Kunne ikke lagre endringer: ${err instanceof Error ? err.message : 'Ukjent feil'}`));

            setBatchEditState(prev => ({
                ...prev,
                [transId]: { ...prev[transId], isSaving: false }
            }));
        }
    };

    const handleBatchFieldSave = async (trans: StcTransDto, field: 'itemBatchNo' | 'itemBatchExpDate') => {
        const transId = `${trans.id}`;
        const editState = batchEditState[transId];
        
        if (!editState || !editState.isEditing) {
            return; // Ingen endringer å lagre
        }
        
        // Valider påkrevde felt
        if ((trans.itemType === 1 || trans.itemType === 5) && !editState.itemBatchNo.trim()) {
            setError(t('warehouse.modules.receipt.validation.batchRequiredForItemType', {}, 'Batchnummer er påkrevd for denne varetypen'));
            return;
        }

        if (trans.itemType === 4 && !editState.itemBatchExpDate) {
            setError(t('warehouse.modules.receipt.validation.expiryRequiredForItemType', {}, 'Utløpsdato er påkrevd for denne varetypen'));
            return;
        }
        
        try {
            setBatchEditState(prev => ({
                ...prev,
                [transId]: { ...prev[transId], isSaving: true }
            }));
            
            // Prøv å bruke den nye updateBatchInfo metoden først
            try {
                await stcTransService.updateBatchInfo(trans.batchID, trans.lineID, {
                    itemBatchNo: editState.itemBatchNo.trim() || undefined,
                    itemBatchExpDate: editState.itemBatchExpDate ? new Date(editState.itemBatchExpDate) : undefined
                });

                console.log('Batch info updated successfully using PATCH method');

            } catch (updateError: any) {
                // Hvis PATCH ikke er støttet, fall tilbake til delete/create
                if (updateError.message === 'UPDATE_NOT_SUPPORTED') {
                    console.log('PATCH not supported, falling back to delete/create method');

                    const updateRequest = buildStcTransUpdateRequest(trans, {
                        itemBatchNo: editState.itemBatchNo.trim() || undefined,
                        itemBatchExpDate: editState.itemBatchExpDate ? new Date(editState.itemBatchExpDate) : undefined
                    });

                    // Delete old transaction and create new one with updated info
                    await stcTransService.deleteTransaction(trans.batchID, trans.lineID);
                    await stcTransService.createTransaction(trans.batchID, updateRequest);

                    console.log('Batch info updated using fallback delete/create method');
                } else {
                    throw updateError;
                }
            }
            
            // Mark as saved
            setBatchEditState(prev => ({
                ...prev,
                [transId]: {
                    ...prev[transId],
                    isEditing: false,
                    isSaving: false
                }
            }));

            // Reload data to show changes
            await loadOrderData();

            // Re-check if receiving can be finished after updating batch info
            if (batchId) {
                await checkCanFinishReceiving(batchId);
            }

        } catch (err) {
            console.error('Error updating batch info:', err);
            setError(t('warehouse.modules.receipt.errors.updateBatchInfoFailed', { error: err instanceof Error ? err.message : t('common.errors.unknownError', {}, 'Ukjent feil') }, `Kunne ikke oppdatere batch-informasjon: ${err instanceof Error ? err.message : 'Ukjent feil'}`));

            setBatchEditState(prev => ({
                ...prev,
                [transId]: { ...prev[transId], isSaving: false }
            }));
        }
    };

    // ============ 36333950: Etikettutskrift per mottakslinje ============
    // Antallsfelt helt til venstre per rad (INGEN forhåndsfylling) + printerikon som
    // skriver ut direkte med operatørens default-printer (backend-resolve: PackZone →
    // Profil → Rapport — printerIdOverride utelates bevisst). Feiler utskriften eller
    // mangler oppsett, åpnes ReceiptPrintModal prefylt som fallback/overstyringsvei.
    const [labelQty, setLabelQty] = useState<Record<string, string>>({});
    const [bulkLabelQty, setBulkLabelQty] = useState('');
    const [printingLabelIds, setPrintingLabelIds] = useState<Set<string>>(new Set());
    const [labelPrintedIds, setLabelPrintedIds] = useState<Set<string>>(new Set());
    const [fallbackPrint, setFallbackPrint] = useState<{
        companyNo: string; itemNo: string; description: string; batch?: string; quantity?: number;
    } | null>(null);

    /** Masse-sett: samme antall på alle aktive mottakslinjer; enkeltlinjer justeres etterpå. */
    const applyBulkLabelQty = () => {
        const value = bulkLabelQty.trim();
        if (!value) return;
        setLabelQty(prev => {
            const next = { ...prev };
            activeReceiptRows.forEach(({ trans }) => { next[`${trans.id}`] = value; });
            return next;
        });
    };

    /** Kopier radens mottatte antall inn i etikettfeltet (én etikett per mottatt enhet). */
    const applyLineQtyToRow = (transId: string, qty?: number | null) => {
        if (!qty || qty <= 0) return;
        setLabelQty(prev => ({ ...prev, [transId]: String(Math.round(qty)) }));
    };

    /** Masse-variant: hver rad får sitt EGET mottatte antall (ikke én felles verdi). */
    const applyLineQtyAll = () => {
        setLabelQty(prev => {
            const next = { ...prev };
            activeReceiptRows.forEach(({ trans }) => {
                if (trans.quantity && trans.quantity > 0) next[`${trans.id}`] = String(Math.round(trans.quantity));
            });
            return next;
        });
    };

    const handleInlineLabelPrint = async (trans: StcTransDto, orderLine: POrderLineDto) => {
        await printLabelsForRow(trans, orderLine, false);
    };

    /**
     * Kjerne for etikettutskrift per mottaksrad.
     * suppressFallback=true brukes av «Skriv alle»: feilende rader skal IKKE åpne
     * hver sin fallback-modal (modal-storm) — bulk-løpet oppsummerer i stedet,
     * og operatøren bruker per-rad-ikonet (som beholder fallback) på resten.
     * 'skippedNoLabel' = varekortet har ReportID -1 («ikke skriv ut etikett»).
     */
    const printLabelsForRow = async (trans: StcTransDto, orderLine: POrderLineDto, suppressFallback: boolean): Promise<'printed' | 'skippedNoLabel' | 'failed'> => {
        const transId = `${trans.id}`;
        const count = parseInt(labelQty[transId] || '', 10);
        if (!count || count <= 0) return 'failed';

        const compNo = orderLine.companyNo || order?.companyNo || '';
        setPrintingLabelIds(prev => new Set(prev).add(transId));
        try {
            // Varens default-etikett hvis satt; ellers lar vi backend falle tilbake til standard.
            let reportId: number | null = null;
            try {
                const itemDefault = await printingService.getItemDefaultReport(compNo, orderLine.itemNo);
                if (itemDefault === -1) {
                    // Varekortet sier «ikke skriv ut etikett»: batch hopper stille over linjen;
                    // eksplisitt enkeltprint åpner dialogen som bevisst manuell overstyring.
                    if (!suppressFallback) {
                        setFallbackPrint({
                            companyNo: compNo,
                            itemNo: orderLine.itemNo,
                            description: orderLine.description1 || '',
                            batch: trans.itemBatchNo || undefined,
                            quantity: count
                        });
                    }
                    return 'skippedNoLabel';
                }
                if (itemDefault && itemDefault > 0) reportId = itemDefault;
            } catch { /* backend-default brukes */ }

            const response = await printingService.printLabel(compNo, {
                reportID: reportId,
                numOfLabels: count,
                itemNo: orderLine.itemNo,
                itemUnitCode: orderLine.itemUnitCode,
                pOrderNo: orderLine.orderNo || order?.orderNo,
                pOrderLineNo: orderLine.lineNo,
                batchNo: trans.itemBatchNo || undefined,
                quantity: trans.quantity || undefined
            });

            if (response.success) {
                setLabelPrintedIds(prev => new Set(prev).add(transId));
                return 'printed';
            }
            // Fallback: åpne print-modalen prefylt — operatøren velger printer/mal manuelt.
            if (!suppressFallback) {
                setFallbackPrint({
                    companyNo: compNo,
                    itemNo: orderLine.itemNo,
                    description: orderLine.description1 || '',
                    batch: trans.itemBatchNo || undefined,
                    quantity: count
                });
            }
            return 'failed';
        } catch (err) {
            console.error('Inline label print failed, opening modal fallback:', err);
            if (!suppressFallback) {
                setFallbackPrint({
                    companyNo: compNo,
                    itemNo: orderLine.itemNo,
                    description: orderLine.description1 || '',
                    batch: trans.itemBatchNo || undefined,
                    quantity: count
                });
            }
            return 'failed';
        } finally {
            setPrintingLabelIds(prev => {
                const next = new Set(prev);
                next.delete(transId);
                return next;
            });
        }
    };

    /**
     * «Skriv alle»: sekvensiell utskrift av alle rader med etikettantall > 0 —
     * antallet styres av hver rads eget felt, ingen dialog. Vellykkede rader
     * markeres grønne som ved enkeltprint; feilende oppsummeres til slutt.
     */
    const [printingAllLabels, setPrintingAllLabels] = useState(false);
    const handlePrintAllLabels = async () => {
        // Kandidater beregnes her (ikke på render-nivå): activeReceiptRows defineres
        // lenger ned i komponenten — referanse under render ville gitt TDZ-feil.
        const candidates = activeReceiptRows.filter(
            ({ trans }) => parseInt(labelQty[`${trans.id}`] || '', 10) > 0
        );
        if (candidates.length === 0 || printingAllLabels) return;
        setPrintingAllLabels(true);
        let okCount = 0;
        let skippedCount = 0;
        try {
            for (const { receivingLine, trans } of candidates) {
                const outcome = await printLabelsForRow(trans, receivingLine.orderLine, true);
                if (outcome === 'printed') okCount++;
                else if (outcome === 'skippedNoLabel') skippedCount++;
            }
        } finally {
            setPrintingAllLabels(false);
        }
        const failedCount = candidates.length - okCount - skippedCount;
        if (failedCount > 0 || skippedCount > 0) {
            const parts: string[] = [];
            if (failedCount > 0) {
                parts.push(t('warehouse.modules.receipt.labelPrint.printAllSummary',
                    { '0': `${okCount}`, '1': `${candidates.length}` },
                    `${okCount} av ${candidates.length} linjer ble sendt til utskrift. Bruk skriverikonet på de gjenværende linjene — det åpner utskriftsdialogen.`));
            }
            if (skippedCount > 0) {
                parts.push(t('warehouse.modules.receipt.labelPrint.printAllSkipped',
                    { '0': `${skippedCount}` },
                    `${skippedCount} linje(r) hoppet over — varen er merket «uten etikett» på varekortet.`));
            }
            alert(parts.join('\n'));
        }
    };

    const handlePrintLabel = async (reportId: number | null, numLabels: number) => {
        if (!selectedSearchLine) {
            setError(t('warehouse.modules.receipt.errors.noItemSelected', {}, 'Ingen vare valgt'));
            return;
        }

        if (!order) {
            setError(t('warehouse.modules.receipt.errors.orderNotLoaded', {}, 'Ordre ikke lastet'));
            return;
        }

        try {
            setError(null);
            // I fritt mottak er order syntetisk med tom companyNo og orderNo=0 —
            // bruk valgt linjes company og linjens eget PO-nummer (ellers mister
            // etiketten all PO-kontekst: prosjektnr, kolli-info, leverandør).
            const response = await printingService.printLabel(selectedSearchLine.companyNo || order.companyNo, {
                reportID: reportId,
                numOfLabels: numLabels,
                itemNo: selectedSearchLine.itemNo,
                itemUnitCode: selectedSearchLine.itemUnitCode,
                pOrderNo: selectedSearchLine.orderNo || order.orderNo,
                pOrderLineNo: selectedSearchLine.lineNo,
                // Add batch info if available (from editingStcTrans or newReceipt)
                batchNo: editingStcTrans?.itemBatchNo,
                expiryDate: editingStcTrans?.itemBatchExpDate
                    ? new Date(editingStcTrans.itemBatchExpDate).toISOString().split('T')[0]
                    : undefined,
                quantity: editingStcTrans?.quantity || newReceipt?.quantity
            });

            if (response.success) {
                // Success - you could show a success message in a success state if needed
                console.log(`${response.labelsProcessed || numLabels} etikett(er) skrevet ut`);
            } else {
                setError(response.message || t('warehouse.modules.receipt.errors.printLabelFailed', {}, 'Feil ved utskrift av etikett'));
            }
        } catch (error: any) {
            console.error('Error printing label:', error);
            setError(error.message || t('warehouse.modules.receipt.errors.printLabelFailed', {}, 'Feil ved utskrift av etikett'));
        }
    };

    const handleStcTransClick = (trans: StcTransDto, orderLine: POrderLineDto) => {
        // Set editing mode for existing StcTrans
        setEditingStcTrans(trans);
        setSelectedSearchLine(orderLine);
        setNewReceipt({
            lineNo: orderLine.lineNo,
            quantity: trans.quantity || 0,
            orderNo: orderLine.orderNo
        });
    };

    // Enhanced state for batch and expiry from barcode
    const [scannedBatchInfo, setScannedBatchInfo] = useState<{ batchNo?: string; expDate?: string; scannedCode?: string }>({});

    // Fokus-signal til FreeReceivingScanPanel: bumpes etter fullført mottak (modal lukket)
    // slik at skannefeltet får fokus igjen og neste vare kan skannes direkte.
    const [scanFocusTick, setScanFocusTick] = useState(0);

    // ============ Fritt mottak (receivingMode='free') ============
    // Kobler en skannet/valgt PO-linje (på tvers av ordrer) inn i den eksisterende mottaksflyten:
    // legger linjen i receivingLines/availableOrderLines og setter selectedSearchLine/newReceipt,
    // slik at ReceivingActiveLine + over-mottak-validering virker som i ordre-modus.
    const handleFreeLineSelected = async (line: POrderLineWithHeaderDto, barcode: ScannedBatchInfo | null) => {
        const orderLineDto = mapHeaderLineToOrderLine(line);

        // Sikre at et fritt mottak (OPEN-batch) finnes før lagring (resume-first).
        let currentBatchId = batchId;
        if (!currentBatchId) {
            currentBatchId = await stcTransService.getOrCreateBatchIdForOpenReceiving();
            setBatchId(currentBatchId);
        }

        const remaining = orderLineDto.remainingQuantity ?? ((orderLineDto.quantity || 0) - (orderLineDto.quantityDelivered || 0));

        // Legg linjen i listene hvis den ikke finnes (så findReceivingLine/validering virker).
        setAvailableOrderLines(prev =>
            prev.some(l => l.orderNo === orderLineDto.orderNo && l.lineNo === orderLineDto.lineNo)
                ? prev
                : [...prev, orderLineDto]
        );
        setReceivingLines(prev =>
            prev.some(rl => rl.orderLine.orderNo === orderLineDto.orderNo && rl.orderLine.lineNo === orderLineDto.lineNo)
                ? prev
                : [...prev, {
                    orderLine: orderLineDto,
                    receivedTransactions: [],
                    totalReceived: orderLineDto.quantityDelivered || 0,
                    remainingQuantity: remaining
                }]
        );

        // Master-data-validering (avanserte lager) før vi starter mottak — som i handleGS1Scan.
        // companyNo-override sikrer validering i fritt mottak der order.companyNo er tom.
        await runMasterDataValidation([orderLineDto.itemNo], () => {
            setEditingStcTrans(null);
            setSelectedSearchLine(orderLineDto);
            setNewReceipt({
                lineNo: orderLineDto.lineNo,
                quantity: barcode?.quantity || remaining || 1,
                orderNo: orderLineDto.orderNo
            });
            setScannedBatchInfo({
                batchNo: barcode?.batchNo,
                expDate: barcode?.expDate,
                scannedCode: barcode?.scannedCode
            });
        }, orderLineDto.companyNo);
    };

    const freeReceiving = useFreeReceiving({
        enabled: receivingMode === 'free',
        onSelectLine: handleFreeLineSelected,
    });

    // Detect if any modal is open to disable global scanning
    const isAnyModalOpen = showPutAwayModal || showEditModal || showDirectReceiptModal ||
                           showLineBrowser || showAdvancedConfigModal || isBulkProcessing || showSerialNumberModal ||
                           freeReceiving.showLineSelector;

    // Check if global barcode scanning is enabled via system setting
    const barcodeScannerEnabled = useBarcodeScannerEnabled();

    // Global barcode scanner - processes scans without requiring input focus
    useSimpleBarcodeScanner({
        enabled: barcodeScannerEnabled && !isAnyModalOpen,
        onScan: (barcode: string) => {
            processGlobalBarcode(barcode);
        }
    });

    /**
     * Find the correct receiving line by orderNo + lineNo composite key.
     * In bilmottak/shipment mode, multiple orders can have the same lineNo,
     * so we must match on both orderNo AND lineNo.
     */
    const findReceivingLine = (lineNo: number, orderNoForLine?: number): ReceivingLine | undefined => {
        if (orderNoForLine !== undefined) {
            // Try composite key first (orderNo + lineNo)
            const match = receivingLines.find(rl => rl.orderLine.orderNo === orderNoForLine && rl.orderLine.lineNo === lineNo);
            if (match) return match;
        }
        // Fallback to lineNo-only (single-order modes like Modus 1)
        return receivingLines.find(rl => rl.orderLine.lineNo === lineNo);
    };

    /**
     * Calculate remaining quantity for a line based on transaction status
     * Remaining = Ordered - PreviouslyDelivered - CurrentBatchReceived - Arrived
     * Accounts for both previous batch deliveries (quantityDelivered) and current batch transactions
     */
    const calculateRemainingQuantity = (line: POrderLineDto, lineNo: number): number => {
        const receivingLine = findReceivingLine(lineNo, line.orderNo);

        if (!receivingLine) {
            // Fallback to backend's remainingQuantity if we don't have transaction data
            return line.remainingQuantity || 0;
        }

        // quantityDelivered is authoritative for all completed deliveries (status >= 50).
        // Only add arrivedQuantity (status 20) which are in-progress, not yet in quantityDelivered.
        const deliveredQty = line.quantityDelivered || 0;

        // Calculate arrivedQuantity (status 20 - UnderArbeid/Ankommet)
        const arrivedQuantity = receivingLine.receivedTransactions
            .filter(t => t.lineStatus === StcTransLineStatus.UnderArbeid)
            .reduce((sum, t) => sum + (t.quantity || 0), 0);

        // Calculate remaining: Ordered - Delivered - Arrived
        return (line.quantity || 0) - deliveredQty - arrivedQuantity;
    };

    /**
     * Validate if receiving quantity would exceed ordered quantity
     * Returns: { allowed: boolean, message: string, isWarning: boolean }
     */
    const validateOverReceiving = (
        orderLine: POrderLineDto,
        newQuantity: number,
        receivingLine?: ReceivingLine,
        editingTransactionQty?: number
    ): { allowed: boolean; message: string; isWarning: boolean } => {
        console.log('🔍 validateOverReceiving called');
        console.log('  - orderLine:', orderLine);
        console.log('  - newQuantity:', newQuantity);
        console.log('  - receivingLine:', receivingLine);
        console.log('  - editingTransactionQty:', editingTransactionQty);

        const orderedQty = orderLine.quantity || 0;
        console.log('  - orderedQty:', orderedQty);

        // quantityDelivered is the authoritative source for all completed deliveries (status >= 50).
        // It already includes quantities from StcTrans with status >= 50, so we must NOT count those again.
        // We only add arrivedQuantity (status 20) which are in-progress receipts not yet in quantityDelivered.
        const deliveredQty = orderLine.quantityDelivered || 0;
        console.log('  - deliveredQty (quantityDelivered):', deliveredQty);

        // Calculate arrived in current batch (status 20: UnderArbeid/Ankommet)
        // These are saved receipt lines not yet finished — NOT included in quantityDelivered
        const arrivedQuantity = receivingLine
            ? receivingLine.receivedTransactions
                .filter(t => t.lineStatus === StcTransLineStatus.UnderArbeid)
                .reduce((sum, t) => sum + (t.quantity || 0), 0)
            : 0;

        // When editing an existing transaction (status 20), subtract its current quantity to avoid double-counting.
        // The existing transaction's qty is already included in arrivedQuantity,
        // and newQuantity is the replacement value, not an addition.
        const editingAdjustment = editingTransactionQty || 0;

        console.log('  - arrivedQuantity (status 20):', arrivedQuantity);
        console.log('  - editingAdjustment (subtract for edit):', editingAdjustment);

        // Total = delivered (includes all status >= 50) + arrived (status 20) - editing old qty + new qty
        const totalAfterReceipt = deliveredQty + arrivedQuantity - editingAdjustment + newQuantity;
        const totalAlreadyReceived = deliveredQty + arrivedQuantity - editingAdjustment;
        // Ankommet-antall netto for linjen som ev. redigeres — brukes i nedbrutt melding
        // slik at operatøren ser HVOR totalen kommer fra (mottatt + ankommet + dette mottaket).
        const arrivedNet = arrivedQuantity - editingAdjustment;
        console.log('  - totalAfterReceipt:', totalAfterReceipt);

        // Check if over-receiving
        if (totalAfterReceipt > orderedQty) {
            const overQty = totalAfterReceipt - orderedQty;
            console.log('  ⚠️  OVER-RECEIVING DETECTED!');
            console.log('  - overQty:', overQty);
            console.log('  - userProfile:', userProfile);
            console.log('  - extraQtyOnGRNotAllowed:', userProfile?.extraQtyOnGRNotAllowed);
            console.log('  - extraQtyOnGRConfirm:', userProfile?.extraQtyOnGRConfirm);

            // Check profile settings:
            // 1. ExtraQtyOnGRNotAllowed = true → HARD BLOCK
            // 2. ExtraQtyOnGRConfirm = true → WARN with confirmation modal
            // 3. Both false → Allow silently
            if (userProfile?.extraQtyOnGRNotAllowed) {
                console.log('  🚫 BLOCKING over-receive');
                // BLOCK completely
                return {
                    allowed: false,
                    message: t('warehouse.modules.receipt.validation.cannotReceiveMoreThanOrdered', {
                        orderedQty,
                        unit: orderLine.itemUnitTxt || t('warehouse.general.unitDefault', {}, 'stk'),
                        deliveredQty,
                        arrivedQty: arrivedNet,
                        newQuantity,
                        totalAfterReceipt,
                        overQty
                    }, `Kan ikke motta mer enn bestilt antall (${orderedQty} ${orderLine.itemUnitTxt || 'stk'}). Tidligere mottatt: ${deliveredQty}, ankommet (ikke ferdigstilt): ${arrivedNet}, dette mottaket: ${newQuantity} → totalt ${totalAfterReceipt} ${orderLine.itemUnitTxt || 'stk'} (${overQty} over).`),
                    isWarning: false
                };
            } else if (userProfile?.extraQtyOnGRConfirm) {
                console.log('  ⚠️  WARNING over-receive (requiring confirmation)');
                // WARN with confirmation modal
                return {
                    allowed: true,
                    message: t('warehouse.modules.receipt.validation.overReceiveWarning', {
                        orderedQty,
                        unit: orderLine.itemUnitTxt || t('warehouse.general.unitDefault', {}, 'stk'),
                        deliveredQty,
                        arrivedQty: arrivedNet,
                        newQuantity,
                        totalAfterReceipt,
                        overQty
                    }, `OBS: Du mottar mer enn bestilt antall. Bestilt: ${orderedQty} ${orderLine.itemUnitTxt || 'stk'}. Tidligere mottatt: ${deliveredQty}, ankommet (ikke ferdigstilt): ${arrivedNet}, dette mottaket: ${newQuantity} → totalt ${totalAfterReceipt} ${orderLine.itemUnitTxt || 'stk'} (${overQty} over).`),
                    isWarning: true
                };
            } else {
                console.log('  ✅ Over-receive allowed silently (no profile restrictions)');
                // Both settings false → Allow silently without warning
                return { allowed: true, message: '', isWarning: false };
            }
        }

        console.log('  ✅ No over-receiving');
        // No over-receiving
        return { allowed: true, message: '', isWarning: false };
    };

    // Process global barcode scan - uses the centralized barcode processing helper
    const processGlobalBarcode = (scanInput: string) => {
        // Fritt mottak: søk på tvers av åpne PO-linjer i stedet for å matche en forhåndslastet ordre.
        if (receivingMode === 'free') {
            freeReceiving.handleScan(scanInput);
            return;
        }
        const result = processBarcodeInput(scanInput);
        if (result) {
            handleGS1Scan({
                itemNo: result.itemNo,
                quantity: result.quantity,
                batchNo: result.batchNo,
                expiryDate: result.expiryDate,
                sscc: result.sscc
            });
        }
    };

    const handleGS1Scan = async (data: { itemNo?: string; quantity?: number; sscc?: string; batchNo?: string; expiryDate?: string }) => {
        console.log('GS1 Scan data received:', data);

        if (!data.itemNo) {
            console.warn('No itemNo in scan data');
            return;
        }

        // Find matching line in availableOrderLines
        // Support GTIN-13/14 matching: AI(01) always returns 14 digits (leading 0 + GTIN-13)
        const itemNoLower = data.itemNo.toLowerCase();
        const findLineByGTIN = (scannedGtin: string) => {
            return availableOrderLines.find(line => {
                const lineItemNo = line.itemNo?.toLowerCase() || '';
                const lineBarCode = line.itemBarCode?.toLowerCase() || '';
                // Exact match
                if (lineItemNo === scannedGtin || lineBarCode === scannedGtin) return true;
                // GTIN-14 scanned vs GTIN-13 stored: pad stored value with leading 0
                if (scannedGtin.length === 14) {
                    if (lineBarCode.length === 13 && '0' + lineBarCode === scannedGtin) return true;
                    if (lineItemNo.length === 13 && '0' + lineItemNo === scannedGtin) return true;
                }
                // GTIN-13 scanned vs GTIN-14 stored: pad scanned value with leading 0
                if (scannedGtin.length === 13) {
                    if (lineBarCode.length === 14 && lineBarCode === '0' + scannedGtin) return true;
                    if (lineItemNo.length === 14 && lineItemNo === '0' + scannedGtin) return true;
                }
                return false;
            });
        };

        let matchedLine = findLineByGTIN(itemNoLower);
        let resolvedItemNo: string | null = null;

        // Fallback: samlet, EKSAKT oppslag i Items + ItemUnits i ETT kall — serveren håndterer
        // GTIN-13/14-variantene (TW 36320099, erstatter fire sekvensielle kall).
        if (!matchedLine) {
            console.log('No direct match on order lines, trying Items/ItemUnits resolve...');
            try {
                const resolved = await itemsService.resolveBarcode(data.itemNo);
                if (resolved.length > 0) {
                    resolvedItemNo = resolved[0].itemNo;
                    console.log('Resolve: Found item via Items/ItemUnits:', resolvedItemNo);

                    const resolvedLower = resolvedItemNo.toLowerCase();
                    matchedLine = availableOrderLines.find(line =>
                        line.itemNo?.toLowerCase() === resolvedLower
                    );
                    if (matchedLine) {
                        console.log('Resolve: Matched order line via resolved ItemNo:', resolvedItemNo);
                    } else {
                        console.warn('Resolve: Resolved ItemNo', resolvedItemNo, 'but no matching order line found');
                    }
                }
            } catch (err) {
                console.error('Barcode resolve failed:', err);
            }
        }

        if (matchedLine) {
            console.log('Matched order line:', matchedLine);
            console.log('Barcode contains batch info:', { batchNo: data.batchNo, expiryDate: data.expiryDate });

            // Validate item master data before starting receipt (advanced warehouses only)
            const lineForReceipt = matchedLine;
            await runMasterDataValidation([lineForReceipt.itemNo], () => {
                // Clear editing mode - this is a new scan
                setEditingStcTrans(null);
                setSelectedSearchLine(lineForReceipt);

                // Set quantity from GS1 data or calculate remaining quantity
                const remainingQty = calculateRemainingQuantity(lineForReceipt, lineForReceipt.lineNo);
                const quantity = data.quantity || remainingQty;
                setNewReceipt({
                    lineNo: lineForReceipt.lineNo,
                    quantity: quantity,
                    orderNo: lineForReceipt.orderNo
                });

                // Store scanned batch info if present (will be picked up by ReceivingActiveLine)
                setScannedBatchInfo({
                    batchNo: data.batchNo,
                    expDate: data.expiryDate
                });
            });
        } else if (!resolvedItemNo && barcodeRegistrationEnabled) {
            // Helt ukjent strekkode (ingen treff i Items eller ItemUnits): tilby registrering på
            // en av ordrens varer (TW 36320099). Etter registrering re-kjøres skannet automatisk.
            console.log('Unknown barcode — offering registration:', data.itemNo);
            setAddBarcodeScanData(data);
            setShowAddBarcodeModal(true);
        } else {
            const gtin13 = data.itemNo.length === 14 && data.itemNo.startsWith('0')
                ? data.itemNo.substring(1) : null;
            const searchedValues = [
                `GTIN: ${data.itemNo}`,
                gtin13 ? `GTIN-13: ${gtin13}` : null,
                resolvedItemNo
                    ? t('warehouse.modules.receipt.detail.lookupFoundItemNotInOrder', { 0: resolvedItemNo }, `Oppslag fant vare: ${resolvedItemNo} (men ikke i ordren)`)
                    : t('warehouse.modules.receipt.detail.noMatchInItemsUnits', {}, 'Ingen treff i Items/ItemUnits')
            ].filter(Boolean).join('\n');

            alert(t('warehouse.modules.receipt.detail.itemNotFoundInOrderSearched', { 0: searchedValues }, `Vare ikke funnet i ordren!\n\nSøkt etter:\n${searchedValues}`));
            console.warn('No matching line found for itemNo:', data.itemNo, 'resolvedItemNo:', resolvedItemNo);
        }
    };

    const getStatusColor = (remainingQuantity: number, totalQuantity: number) => {
        if (remainingQuantity === 0) return 'bg-green-100 text-green-800';
        if (remainingQuantity < totalQuantity) return 'bg-yellow-100 text-yellow-800';
        return 'bg-gray-100 text-gray-800';
    };

    const getStatusText = (remainingQuantity: number, totalQuantity: number) => {
        if (remainingQuantity === 0) return t('warehouse.modules.receipt.status.complete', {}, 'Komplett');
        if (remainingQuantity < totalQuantity) return t('warehouse.modules.receipt.status.partiallyReceived', {}, 'Delvis mottatt');
        return t('warehouse.modules.receipt.status.notReceived', {}, 'Ikke mottatt');
    };

    // Add PO to bilmottak handlers
    const handleOpenAddPO = async () => {
        if (!bilmottakBatchId) return;
        try {
            setAddPOLoading(true);
            const orders = await pOrdersService.getOrdersForReceiving();
            // Filter out orders already linked to this batch
            const linkedOrderNos = new Set(receivingLines.map(l => l.orderLine.orderNo));
            const available = orders.filter(
                o => !linkedOrderNos.has(o.orderNo) && (!o.warehouse || o.warehouse === (bilmottakInfo?.warehouse || ''))
            );
            setAddPOAvailableOrders(available);
            setAddPOSelectedNos(new Set());
            setAddPOSearchTerm('');
            setShowAddPOModal(true);
        } catch (err) {
            console.error('Error loading orders for add PO:', err);
        } finally {
            setAddPOLoading(false);
        }
    };

    const handleConfirmAddPO = async () => {
        if (!bilmottakBatchId || addPOSelectedNos.size === 0) return;
        try {
            setAddPOLoading(true);
            const result = await bilmottakService.linkOrders(bilmottakBatchId, Array.from(addPOSelectedNos));
            if (result.success) {
                setShowAddPOModal(false);
                // Reload order lines to include newly linked POs
                await loadOrderData(true);
            } else {
                setError(result.errorMessage || t('warehouse.modules.receipt.detail.linkOrdersFailed', {}, 'Kunne ikke koble ordrer'));
            }
        } catch (err) {
            console.error('Error linking orders:', err);
            setError(t('warehouse.modules.receipt.detail.linkOrdersError', {}, 'Feil ved kobling av ordrer'));
        } finally {
            setAddPOLoading(false);
        }
    };

    const filteredAddPOOrders = addPOAvailableOrders.filter(o => {
        if (!addPOSearchTerm.trim()) return true;
        const term = addPOSearchTerm.toLowerCase();
        return o.orderNoERP?.toLowerCase().includes(term) ||
            o.supplierName?.toLowerCase().includes(term) ||
            String(o.orderNo).includes(term);
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="flex items-center space-x-2">
                    <RefreshCw className="h-6 w-6 animate-spin text-blue-500" />
                    <span className="text-lg text-gray-600">{t('warehouse.modules.receipt.messages.loadingOrderInfo', {}, 'Henter ordre-informasjon...')}</span>
                </div>
            </div>
        );
    }

    if (showSuccessMessage) {
        return (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                <div className="flex items-center space-x-2">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <div>
                        <h3 className="text-green-800 font-medium">{t('warehouse.modules.receipt.messages.receivingComplete', {}, 'Mottak avsluttet!')}</h3>
                        <p className="text-green-700 text-sm mt-1">
                            {t('warehouse.modules.receipt.messages.returningToList', {}, 'Status er endret til "Klar for eksport". Returnerer til mottakslisten...')}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    if (!order) {
        return (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                <div className="flex items-center space-x-2">
                    <AlertCircle className="h-5 w-5 text-yellow-500" />
                    <span className="text-yellow-700">{t('warehouse.modules.receipt.errors.orderNotFound', {}, 'Ordre ikke funnet')}</span>
                </div>
            </div>
        );
    }

    // Ankomstregistrerte (åpne) transaksjoner, flatet ut på tvers av mottakslinjer.
    // Hoistet ut av JSX fordi både tabellen (>= lg) og kortlista (< lg) trenger samme datasett.
    const activeReceiptRows = receivingLines.flatMap((receivingLine) =>
        receivingLine.receivedTransactions
            .filter(trans => trans.lineStatus < StcTransLineStatus.KlarForEksport)
            .map((trans) => ({ receivingLine, trans }))
    );

    // Fullførte linjer: ordre-/forsendelsesmottak = fullt levert (QuantityDelivered >= bestilt,
    // kumulativt på tvers av batcher/direkte utlegg); fritt mottak/bilmottak = virtuelle linjer
    // → fullførte transaksjoner (LineStatus >= 50).
    const isLineCompleted = (line: ReceivingLine) =>
        (receivingMode === 'free' || receivingMode === 'bilmottak')
            ? line.receivedTransactions.some(trans => trans.lineStatus >= StcTransLineStatus.KlarForEksport)
            : ((line.orderLine.quantity || 0) > 0 && (line.orderLine.quantityDelivered || 0) >= (line.orderLine.quantity || 0));

    const completedReceiptLines = (() => {
        const grouped = receivingLines
            .filter(isLineCompleted)
            .map(receivingLine => ({
                key: `${receivingLine.orderLine.orderNo}-${receivingLine.orderLine.lineNo}-${receivingLine.orderLine.itemNo}`,
                orderLine: receivingLine.orderLine,
                // Antall fullførte transaksjoner for denne linjen (gjeldende batch)
                transactionCount: receivingLine.receivedTransactions.filter(trans => trans.lineStatus >= StcTransLineStatus.KlarForEksport).length,
                // Sum av fullførte (status >= 50) mengder — brukes som «Mottatt» i virtuelle-linje-modi
                // (fritt mottak/bilmottak) der orderLine.quantityDelivered er en syntetisk 0.
                completedQty: receivingLine.receivedTransactions
                    .filter(trans => trans.lineStatus >= StcTransLineStatus.KlarForEksport)
                    .reduce((sum, trans) => sum + (trans.quantity || 0), 0),
                // ERP-ordrenr for visning: virtuelle linjer (fritt/bilmottak) mangler
                // orderLine.orderNoERP, så hent fra en fullført transaksjon (som aktiv-tabellen).
                displayOrderNo: (receivingMode === 'free' || receivingMode === 'bilmottak')
                    ? (receivingLine.receivedTransactions.find(trans => trans.lineStatus >= StcTransLineStatus.KlarForEksport)?.orderNoERP
                        || receivingLine.orderLine.orderNoERP || receivingLine.orderLine.orderNo)
                    : (receivingLine.orderLine.orderNoERP || receivingLine.orderLine.orderNo)
            }));

        // Fjern duplikater (samme ordrelinje kan forekomme flere ganger)
        return grouped.filter((line, index, self) => index === self.findIndex(l => l.key === line.key));
    })();

    /** «Mottatt»-mengde for en fullført linje — se kommentaren over completedReceiptLines. */
    const completedDeliveredQty = (line: typeof completedReceiptLines[number]) =>
        (receivingMode === 'free' || receivingMode === 'bilmottak')
            ? line.completedQty
            : (line.orderLine.quantityDelivered || 0);

    return (
        <div className="space-y-6">
            {/* Mobile/Enterprise scanner support (Zebra, etc.) - invisible input that captures scanner data */}
            <MobileBarcodeCaptureInput
                enabled={barcodeScannerEnabled && !isAnyModalOpen}
                onScan={(barcode) => {
                    console.log('📱 Mobile scanner detected barcode:', barcode);
                    processGlobalBarcode(barcode);
                }}
            />

            {/* Bulk processing overlay */}
            {isBulkProcessing && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-8 max-w-md">
                        <div className="flex items-center space-x-4">
                            <RefreshCw className="h-8 w-8 text-blue-500 animate-spin" />
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900">{t('warehouse.modules.receipt.messages.processingReceipt', {}, 'Prosesserer mottak')}</h3>
                                <p className="text-sm text-gray-600 mt-1">{bulkProcessingMessage}</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Header - Compact design with buttons on same line as back */}
            <div className="space-y-2">
                {/* Top row: Back button + Action buttons */}
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                        <BackButton onClick={onBack} className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200" hideLabelOnMobile />
                        <MediaCaptureButton
                            entityType={receivingMode === 'bilmottak' ? 'Bilmottak' : 'POrder'}
                            entityKey={receivingMode === 'bilmottak' ? (bilmottakBatchId || '') : (orderNo?.toString() || '')}
                        />
                    </div>

                    {/* Action buttons on right — full rad fra `lg` og opp (uendret desktop-oppsett).
                        Under `lg` er det ikke plass til 7-8 knapper; se den kompakte varianten under. */}
                    <div className="hidden lg:flex items-center gap-2">
                        {receivingMode === 'bilmottak' && bilmottakBatchId && (
                            <button
                                onClick={handleOpenAddPO}
                                disabled={addPOLoading}
                                className="flex items-center gap-1 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm"
                                title={t('warehouse.modules.receipt.detail.addPurchaseOrders', {}, 'Legg til innkjøpsordrer')}
                            >
                                <Plus className="h-4 w-4" />
                                <span className="hidden sm:inline">{t('warehouse.modules.receipt.detail.addOrder', {}, 'Legg til ordre')}</span>
                            </button>
                        )}
                        {receivingMode !== 'free' && (
                            <button
                                onClick={() => setShowLineBrowser(true)}
                                className="flex items-center gap-1 px-3 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors text-sm"
                                title={t('warehouse.modules.receipt.actions.viewAllLines', {}, 'Se alle ordrelinjer')}
                            >
                                <List className="h-4 w-4" />
                                <span className="hidden sm:inline">{t('warehouse.modules.receipt.labels.lines', {}, 'Linjer')}</span>
                            </button>
                        )}
                        <button
                            onClick={() => loadOrderData()}
                            className="flex items-center gap-1 px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm"
                            title={t('common.buttons.refresh', {}, 'Oppdater')}
                        >
                            <RefreshCw className="h-4 w-4" />
                        </button>

                        {/* Sorting mode toggle */}
                        <button
                            onClick={handleToggleSortingMode}
                            className={`flex items-center gap-1 px-3 py-2 rounded-lg transition-colors text-sm ${
                                sortingModeActive
                                    ? 'bg-orange-500 text-white hover:bg-orange-600 ring-2 ring-orange-300'
                                    : 'bg-white text-orange-600 border-2 border-orange-400 hover:bg-orange-50'
                            }`}
                            title={sortingModeActive
                                ? t('warehouse.modules.receipt.deactivateSorting')
                                : t('warehouse.modules.receipt.activateSorting')
                            }
                        >
                            <PackageSearch className="h-4 w-4" />
                            <span className="hidden sm:inline">
                                {t('warehouse.modules.receipt.sortingMode')}
                            </span>
                        </button>

                        {/* Print sorting labels - only in sorting mode */}
                        {sortingModeActive && (
                            <button
                                onClick={handlePrintAllSortingLabels}
                                className="flex items-center gap-1 px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm"
                                title={t('warehouse.modules.receipt.printSortingLabels')}
                            >
                                <Package className="h-4 w-4" />
                                <span className="hidden sm:inline">{t('warehouse.modules.receipt.printSortingLabels')}</span>
                            </button>
                        )}

                        {hasTransactions && (
                            <>
                                <button
                                    onClick={handleFinishReceiving}
                                    disabled={!canFinishReceiving || isFinishingReceiving}
                                    className={`flex items-center gap-1 px-3 py-2 rounded-lg transition-colors text-sm ${
                                        canFinishReceiving && !isFinishingReceiving
                                            ? 'bg-green-500 text-white hover:bg-green-600'
                                            : 'bg-gray-400 text-gray-700 cursor-not-allowed'
                                    }`}
                                    title={!canFinishReceiving ? t('warehouse.modules.receipt.messages.cannotFinishYet', {}, 'Mottaket kan ikke avsluttes ennå') : t('warehouse.modules.receipt.actions.finishReceiving', {}, 'Avslutt mottak')}
                                >
                                    {isFinishingReceiving ? (
                                        <RefreshCw className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <Check className="h-4 w-4" />
                                    )}
                                    <span className="hidden sm:inline">{isFinishingReceiving ? t('warehouse.modules.receipt.actions.finishing', {}, 'Avslutter...') : t('warehouse.modules.receipt.actions.finish', {}, 'Avslutt')}</span>
                                </button>

                                <button
                                    onClick={handleFinishReceivingAndPutAway}
                                    disabled={!canFinishReceiving || isFinishingReceiving}
                                    className={`flex items-center gap-1 px-3 py-2 rounded-lg transition-colors text-sm ${
                                        canFinishReceiving && !isFinishingReceiving
                                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                                            : 'bg-gray-400 text-gray-700 cursor-not-allowed'
                                    }`}
                                    title={!canFinishReceiving ? t('warehouse.modules.receipt.messages.cannotFinishYet', {}, 'Mottaket kan ikke avsluttes ennå') : t('warehouse.modules.receipt.actions.finishAndPutAway', {}, 'Avslutt og gå til plassering')}
                                >
                                    {isFinishingReceiving ? (
                                        <RefreshCw className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <ArrowRight className="h-4 w-4" />
                                    )}
                                    <span className="hidden sm:inline">{isFinishingReceiving ? t('warehouse.modules.receipt.actions.processing', {}, 'Prosesserer...') : t('warehouse.modules.receipt.actions.receiptAndPutAway', {}, 'Mottak & Plassering')}</span>
                                </button>
                            </>
                        )}
                    </div>

                    {/* Kompakt handlingsrad under `lg` (nettbrett i portrett, håndholdte terminaler).
                        Oppdater, ordrelinjer og sorteringsmodus står synlig: ordrelinjer fordi det er
                        den hyppigst brukte sekundærhandlingen, sorteringsmodus fordi det er en tilstand
                        operatøren må kunne se er på. Overflow-menyen tar bare det som er modus-spesifikt
                        (legg til ordre, skriv ut sorteringsetiketter) og forsvinner når den er tom — i
                        ordremottak er den derfor normalt ikke synlig. De to avslutt-handlingene ligger i
                        handlingslinjen nederst, innen tommelrekkevidde. */}
                    <div className="flex lg:hidden items-center gap-1.5">
                        <button
                            onClick={() => loadOrderData()}
                            aria-label={t('common.buttons.refresh', {}, 'Oppdater')}
                            title={t('common.buttons.refresh', {}, 'Oppdater')}
                            className="flex items-center justify-center min-h-[44px] min-w-[44px] px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                        >
                            <RefreshCw className="h-4 w-4" />
                        </button>

                        {receivingMode !== 'free' && (
                            <button
                                onClick={() => setShowLineBrowser(true)}
                                aria-label={t('warehouse.modules.receipt.actions.viewAllLines', {}, 'Se alle ordrelinjer')}
                                title={t('warehouse.modules.receipt.actions.viewAllLines', {}, 'Se alle ordrelinjer')}
                                className="flex items-center justify-center min-h-[44px] min-w-[44px] px-3 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
                            >
                                <List className="h-4 w-4" />
                            </button>
                        )}

                        <button
                            onClick={handleToggleSortingMode}
                            aria-pressed={sortingModeActive}
                            aria-label={sortingModeActive
                                ? t('warehouse.modules.receipt.deactivateSorting')
                                : t('warehouse.modules.receipt.activateSorting')
                            }
                            title={sortingModeActive
                                ? t('warehouse.modules.receipt.deactivateSorting')
                                : t('warehouse.modules.receipt.activateSorting')
                            }
                            className={`flex items-center justify-center min-h-[44px] min-w-[44px] px-3 py-2 rounded-lg transition-colors ${
                                sortingModeActive
                                    ? 'bg-orange-500 text-white hover:bg-orange-600 ring-2 ring-orange-300'
                                    : 'bg-white text-orange-600 border-2 border-orange-400 hover:bg-orange-50'
                            }`}
                        >
                            <PackageSearch className="h-4 w-4" />
                        </button>

                        <ReceiptActionMenu
                            label={t('warehouse.modules.receipt.actions.moreActions', {}, 'Flere handlinger')}
                            items={[
                                ...(receivingMode === 'bilmottak' && bilmottakBatchId ? [{
                                    key: 'add-po',
                                    label: t('warehouse.modules.receipt.detail.addPurchaseOrders', {}, 'Legg til innkjøpsordrer'),
                                    icon: <Plus className="h-4 w-4" />,
                                    onClick: handleOpenAddPO,
                                    disabled: addPOLoading
                                }] : []),
                                ...(sortingModeActive ? [{
                                    key: 'print-sorting',
                                    label: t('warehouse.modules.receipt.printSortingLabels'),
                                    icon: <Package className="h-4 w-4" />,
                                    onClick: handlePrintAllSortingLabels
                                }] : [])
                            ] as ReceiptActionMenuItem[]}
                        />
                    </div>
                </div>

                {/* Order info below buttons, aligned left */}
                <div className="ml-0">
                    {receivingMode === 'bilmottak' ? (
                        <>
                            <h1 className="text-lg font-bold text-purple-700 flex items-center gap-2">
                                <span className="inline-flex items-center px-2 py-0.5 rounded bg-purple-100 text-purple-700 text-xs font-medium">
                                    {t('warehouse.modules.receipt.detail.truckReceiving', {}, 'Bilmottak')}
                                </span>
                                {/* min-w-0 + truncate: en batch-ID uten mellomrom (GUID) ville ellers
                                    satt en minimumsbredde for hele siden på håndholdte terminaler. */}
                                <span className="min-w-0 truncate">{bilmottakInfo?.name || bilmottakBatchId}</span>
                            </h1>
                            <p className="text-sm text-gray-600">{bilmottakInfo?.warehouse || t('warehouse.modules.receipt.detail.multiUserReceiving', {}, 'Flerbruker-mottak')}</p>
                        </>
                    ) : receivingMode === 'shipment' ? (
                        <>
                            <h1 className="text-lg font-bold text-teal-700 flex items-center gap-2">
                                <span className="inline-flex items-center px-2 py-0.5 rounded bg-teal-100 text-teal-700 text-xs font-medium">
                                    {t('warehouse.modules.receipt.detail.shipment', {}, 'Forsendelse')}
                                </span>
                                <span className="min-w-0 truncate">{shipmentInfo?.shipmentNo || inboundShipmentNo}</span>
                            </h1>
                            <p className="text-sm text-gray-600">{shipmentInfo?.supplierNames || order.supplierName}</p>
                        </>
                    ) : receivingMode === 'free' ? (
                        <>
                            <h1 className="text-lg font-bold text-emerald-700 flex items-center gap-2">
                                <span className="inline-flex items-center px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 text-xs font-medium">
                                    {t('warehouse.modules.receipt.detail.freeReceiving', {}, 'Fritt mottak')}
                                </span>
                            </h1>
                            <p className="text-sm text-gray-600">{t('warehouse.modules.receipt.detail.freeReceivingDescription', {}, 'Skann varer på tvers av innkjøpsordrer')}</p>
                        </>
                    ) : (
                        <>
                            <h1 className="text-lg font-bold text-gray-900 break-words">
                                {t('warehouse.modules.receipt.orderHeader', { orderNo: order.orderNoERP }, `Ordre ${order.orderNoERP}`)}{order.supplierOrderNo && ` | ${order.supplierOrderNo}`}
                            </h1>
                            <p className="text-sm text-gray-600">{order.supplierName}</p>
                        </>
                    )}
                </div>
            </div>

            {/* GS1 Scanner - Enhanced with full GS1-128 and plain barcode support */}
            {receivingMode !== 'free' && (
                <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
                    <div className="flex gap-2">
                        <ScanTextInput
                            ref={scannerRef}
                            placeholder={t('warehouse.modules.receipt.placeholders.scanBarcode', {}, 'Skann strekkode (GS1-128 eller vanlig varenummer)')}
                            className="flex-1 p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    const target = e.target as HTMLInputElement;
                                    const value = target.value.trim();
                                    if (value) {
                                        processGlobalBarcode(value);
                                        target.value = '';
                                    }
                                }
                            }}
                        />
                    </div>
                </div>
            )}

            {/* Fritt mottak: søkefelt (skann på tvers av åpne PO-linjer) + velgmodal. Global/mobil
                skanner håndteres allerede av container (processGlobalBarcode → free.handleScan),
                derfor er panelets egen mobil-fangst deaktivert. */}
            {receivingMode === 'free' && (
                <FreeReceivingScanPanel
                    free={freeReceiving}
                    barcodeScannerEnabled={false}
                    hidden={!!selectedSearchLine || !!editingStcTrans}
                    focusTick={scanFocusTick}
                />
            )}

            {/* Error message - vises inline, ikke som early return */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                            <AlertCircle className="h-5 w-5 text-red-500" />
                            <span className="text-red-700">{error}</span>
                        </div>
                        <button
                            onClick={() => setError(null)}
                            className="text-red-500 hover:text-red-700"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            )}

            {/* Put-away retry — grunnlagsrader (StcIntRelocation) der plassering-generering
                feilet ved ferdigstilling. Synlig også når batchen er lukket. */}
            {pendingPutAwayItems.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4 mb-4">
                    <div className="flex items-start gap-2">
                        <AlertCircle className="h-5 w-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                            <h3 className="text-yellow-800 font-medium">
                                {t('warehouse.modules.receipt.detail.putAwayPendingTitle', { 0: String(pendingPutAwayItems.length) }, `Plassering feilet for ${pendingPutAwayItems.length} vare(r)`)}
                            </h3>
                            <p className="text-yellow-700 text-sm mt-1">
                                {t('warehouse.modules.receipt.detail.putAwayPendingDescription', {}, 'Varene er mottatt, men står uten plasseringsoppgave på mottakslokasjonen. Rett opp lokasjonskonfigurasjonen for varene og generer plassering på nytt.')}
                            </p>
                            <ul className="mt-2 space-y-1">
                                {pendingPutAwayItems.map(item => (
                                    <li key={item.relID} className="text-sm text-yellow-800">
                                        <span className="font-medium">{item.itemNo}</span>
                                        {item.itemText && <span> — {item.itemText}</span>}
                                        <span className="text-yellow-700"> ({item.quantity} @ {item.binCode})</span>
                                    </li>
                                ))}
                            </ul>
                            {retryPutAwayMessage && (
                                <p className="text-sm text-yellow-800 font-medium mt-2">{retryPutAwayMessage}</p>
                            )}
                            <button
                                onClick={handleRetryPutAway}
                                disabled={isRetryingPutAway}
                                className={`mt-3 flex items-center gap-2 px-4 py-3 min-h-[44px] rounded-lg transition-colors text-sm font-medium ${
                                    isRetryingPutAway
                                        ? 'bg-gray-400 text-gray-700 cursor-not-allowed'
                                        : 'bg-blue-600 text-white hover:bg-blue-700'
                                }`}
                            >
                                {isRetryingPutAway ? (
                                    <RefreshCw className="h-4 w-4 animate-spin" />
                                ) : (
                                    <ArrowRight className="h-4 w-4" />
                                )}
                                {isRetryingPutAway
                                    ? t('warehouse.modules.receipt.detail.retryPutAwayRunning', {}, 'Genererer plassering...')
                                    : t('warehouse.modules.receipt.detail.retryPutAwayButton', {}, 'Generer plassering på nytt')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Kvittering etter vellykket retry (panelet over forsvinner når alt er løst) */}
            {pendingPutAwayItems.length === 0 && retryPutAwayMessage && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                            <CheckCircle className="h-5 w-5 text-green-500" />
                            <span className="text-green-700">{retryPutAwayMessage}</span>
                        </div>
                        <button
                            onClick={() => setRetryPutAwayMessage(null)}
                            className="text-green-500 hover:text-green-700 p-2"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            )}

            {/* Warning Message */}
            {warningMessage && (
                <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-4">
                    <div className="flex items-center">
                        <AlertCircle className="h-5 w-5 text-yellow-500 mr-2" />
                        <p className="text-yellow-700">{warningMessage}</p>
                    </div>
                </div>
            )}

            {/* Active Line Component */}
            {selectedSearchLine && newReceipt && (() => {
                // Calculate quantities from transactions for the selected line
                const receivingLine = findReceivingLine(newReceipt.lineNo, newReceipt.orderNo ?? selectedSearchLine?.orderNo);

                // Quantity delivered in previous completed batches
                const previouslyDelivered = selectedSearchLine.quantityDelivered || 0;

                // Only sum transactions with status = 20 (UnderArbeid) for arrivedQuantity
                const arrivedQuantity = receivingLine
                    ? receivingLine.receivedTransactions
                        .filter(t => t.lineStatus === StcTransLineStatus.UnderArbeid)
                        .reduce((sum, t) => sum + (t.quantity || 0), 0)
                    : 0;

                // quantityDelivered is authoritative for all completed deliveries (status >= 50)
                // Don't separately count currentBatch status >= 50 — already in quantityDelivered
                const totalReceived = previouslyDelivered;

                // Calculate remainingQuantity: Ordered - Delivered - Arrived
                const remainingQuantity = (selectedSearchLine.quantity || 0) - previouslyDelivered - arrivedQuantity;

                return (
                    <ReceivingActiveLine
                        line={{
                            itemNo: selectedSearchLine.itemNo,
                            description1: selectedSearchLine.description1 || t('common.noDescription', {}, 'Ingen beskrivelse'),
                            quantity: selectedSearchLine.quantity || 0,
                            totalReceived: totalReceived,
                            arrivedQuantity: arrivedQuantity,
                            remainingQuantity: remainingQuantity,
                            itemUnitTxt: selectedSearchLine.itemUnitTxt || t('warehouse.general.unitDefault', {}, 'stk'),
                            itemType: selectedLineItemType
                        }}
                        extraInfoValues={resolveReceiptExtraInfoValues(selectedSearchLine, extraInfoFields)}
                        editingStcTrans={editingStcTrans ? {
                            itemBatchNo: editingStcTrans.itemBatchNo || '',
                            itemBatchExpDate: editingStcTrans.itemBatchExpDate ? new Date(editingStcTrans.itemBatchExpDate).toISOString().split('T')[0] : '',
                            quantity: editingStcTrans.quantity || 0,
                            itemType: editingStcTrans.itemType,
                            locationName: editingStcTrans.locationName || ''
                        } : null}
                        currentQuantity={editingStcTrans ? editingStcTrans.quantity || 0 : newReceipt.quantity}
                        scannedBatchInfo={scannedBatchInfo}
                        validationError={selectedSearchLine.itemExistsInMaster === false
                            ? t('warehouse.modules.receipt.validation.itemNotInMaster', { 0: selectedSearchLine.itemNo }, `Vare ${selectedSearchLine.itemNo} finnes ikke i varemaster — kan ikke mottas. Importer/opprett varen først.`)
                            : validationError}
                        validationWarning={validationWarning}
                        onQuantityChange={(qty) => {
                            // Clear validation when quantity changes
                            setValidationError('');
                            setValidationWarning('');

                            if (editingStcTrans) {
                                // When editing, update the editingStcTrans object directly
                                setEditingStcTrans({ ...editingStcTrans, quantity: qty });
                            } else {
                                // When creating new, update newReceipt
                                setNewReceipt({ ...newReceipt, quantity: qty });
                            }
                        }}
                        onBatchInfoChange={(batchNo, expDate) => {
                            if (editingStcTrans) {
                                setEditingStcTrans({
                                    ...editingStcTrans,
                                    itemBatchNo: batchNo,
                                    itemBatchExpDate: expDate ? new Date(expDate) : undefined
                                });
                            }
                            // For new receipts, batch info is handled internally in ReceivingActiveLine
                        }}
                        onSave={(qty, batchNo, expDate, receivingLocation) => {
                            handleAddReceipt(newReceipt.lineNo, qty, batchNo, expDate, receivingLocation);
                            // Clear scanned batch info after save
                            setScannedBatchInfo({});
                        }}
                        onCancel={() => {
                            setNewReceipt(null);
                            setSelectedSearchLine(null);
                            setEditingStcTrans(null);
                            setScannedBatchInfo({});
                            setValidationError('');
                            setValidationWarning('');
                        }}
                        onDirectReceipt={editingStcTrans
                            ? (qty, batchNo, expDate, receivingLocation) => {
                                // For editing existing: validate over-receiving before opening direktemottak modal
                                const rl = findReceivingLine(editingStcTrans.lineNo!, selectedSearchLine?.orderNo);
                                if (rl?.orderLine) {
                                    const origQty = rl.receivedTransactions.find(t => t.lineID === editingStcTrans.lineID)?.quantity || 0;
                                    const validation = validateOverReceiving(rl.orderLine, qty, rl, origQty);
                                    if (!validation.allowed) {
                                        setValidationError(validation.message);
                                        setValidationWarning('');
                                        return;
                                    } else if (validation.isWarning) {
                                        setOverReceiveModalMessage(validation.message);
                                        setPendingOverReceiveAction(() => async () => {
                                            handleDirectReceipt(editingStcTrans);
                                        });
                                        setShowOverReceiveConfirmModal(true);
                                        return;
                                    }
                                }
                                handleDirectReceipt(editingStcTrans);
                            }
                            : (qty, batchNo, expDate, receivingLocation) => {
                                // For new receipts: create StcTrans first, then open direktemottak modal
                                handleDirectReceiptFromNew(newReceipt.lineNo, qty, batchNo, expDate, receivingLocation);
                                // Clear scanned batch info after direct receipt
                                setScannedBatchInfo({});
                            }
                        }
                        onDelete={editingStcTrans ? () => handleDeleteReceipt(editingStcTrans.lineID, editingStcTrans.id!) : undefined}
                        showImage={false}
                        onOpenGrunndata={allowAdvItemConfig ? () => handleOpenAdvancedConfig(selectedSearchLine.itemNo) : undefined}
                        onPrintLabel={(reportId, numLabels) => handlePrintLabel(reportId, numLabels)}
                        showPrintSection={true}
                        pOrderNo={selectedSearchLine.orderNo || order.orderNo}
                        pOrderLineNo={selectedSearchLine.lineNo}
                        companyNo={selectedSearchLine.companyNo || order.companyNo}
                        warehouseID={selectedSearchLine.warehouse || order.warehouse || 'MAIN'}
                        defaultReceivingLocation={lastUsedReceivingLocation ?? userProfile?.defaultGRBinCode}
                        sortingModeActive={sortingModeActive}
                        expectedSortingBox={getSortingBoxInfoForItem(selectedSearchLine.itemNo)}
                        onCloseSortingBox={handleCloseSortingBox}
                    />
                );
            })()}

            {/* Combined Search Box replaced with GS1Scanner + ActiveLine above */}

            {/* Table layout with sticky header */}
            {(receivingLines.some(line => line.receivedTransactions.length > 0 || (line.orderLine.quantityDelivered || 0) > 0) || newReceipt) && (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    {/* Kortvisning under `lg` (nettbrett i portrett, håndholdte terminaler).
                        Tabellen med 5-6 kolonner krever horisontal scrolling på ~360 px; kortet viser
                        de samme feltene, men slår «Bestilt» og «Ankommet» sammen til ett forholdstall.
                        Kolonneoverskriftene erstattes av én forklaring øverst i lista. */}
                    {activeReceiptRows.length > 0 && (
                        <div className="lg:hidden">
                            <div className="flex items-center justify-between gap-2 px-3 py-2 bg-blue-100 border-b border-blue-200">
                                <span className="text-xs font-semibold text-blue-800 uppercase tracking-wider truncate">
                                    {t('warehouse.modules.receipt.labels.arrivalRegistered', { count: activeReceiptRows.length }, `Ankomstregistrert (${activeReceiptRows.length})`)}
                                </span>
                                <span className="flex-shrink-0 text-[10px] font-medium text-blue-800 uppercase tracking-wider opacity-75">
                                    {t('warehouse.modules.receipt.fields.arrived', {}, 'Ankommet')} / {t('warehouse.modules.receipt.fields.ordered', {}, 'Bestilt')}
                                </span>
                            </div>
                            {/* bg-gray-50 bak kortene: uten den blir hvite kort liggende på hvitt panel */}
                            <div className="p-3 space-y-2 bg-gray-50">
                                {activeReceiptRows.map(({ receivingLine, trans }) => {
                                    // Samme antallslogikk som tabellen: en pågående kvantumsredigering
                                    // vinner over den lagrede transaksjonsmengden.
                                    const cachedEditState = batchEditState[`${trans.id}`];
                                    const arrivedQty = cachedEditState?.isEditingQuantity ? cachedEditState.quantity : (trans.quantity || 0);

                                    return (
                                        <ReceiptLineCard
                                            key={`card-${receivingLine.orderLine.orderNo}-${receivingLine.orderLine.lineNo}-${trans.id}`}
                                            itemNo={receivingLine.orderLine.itemNo}
                                            description={receivingLine.orderLine.description1 || t('common.noDescription', {}, 'Ingen beskrivelse')}
                                            quantity={arrivedQty}
                                            orderedQuantity={receivingLine.orderLine.quantity || 0}
                                            unit={receivingLine.orderLine.itemUnitTxt}
                                            location={trans.locationName}
                                            orderNo={receivingMode !== 'order'
                                                ? (trans.orderNoERP || trans.orderNo || receivingLine.orderLine.orderNoERP || receivingLine.orderLine.orderNo)
                                                : null}
                                            serialNo={trans.itemSerialNo}
                                            extraInfoValues={resolveReceiptExtraInfoValues(receivingLine.orderLine, extraInfoFields)}
                                            variant="arrived"
                                            onClick={() => handleStcTransClick(trans, receivingLine.orderLine)}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <div className="hidden lg:block overflow-x-auto">
                        <table className="min-w-full">
                            {/* Sticky header */}
                            <thead className="bg-blue-100 sticky top-0 z-10">
                                <tr>
                                    {/* 36333950: etiketter per linje — masse-sett i header, antall + print per rad */}
                                    <th className="px-2 py-2 text-left text-xs font-semibold text-blue-800 uppercase tracking-wider whitespace-nowrap">
                                        <div className="flex flex-col gap-1">
                                            <span>{t('warehouse.modules.receipt.labelPrint.columnHeader', {}, 'Etiketter')}</span>
                                            <div className="flex items-center gap-1">
                                                <NumericInput
                                                    mode="integer"
                                                    value={bulkLabelQty}
                                                    onChange={(e) => setBulkLabelQty(e.target.value)}
                                                    placeholder={t('warehouse.modules.receipt.labelPrint.bulkPlaceholder', {}, 'Alle')}
                                                    className="w-14 px-1.5 py-1 text-xs font-normal normal-case border border-blue-300 rounded bg-white text-gray-900"
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); applyBulkLabelQty(); }}
                                                    disabled={!bulkLabelQty.trim()}
                                                    className="px-1.5 py-1 text-[10px] font-medium normal-case bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-300 min-h-[28px]"
                                                    title={t('warehouse.modules.receipt.labelPrint.bulkApplyTooltip', {}, 'Sett dette antallet på alle linjer — juster deretter linjene som avviker')}
                                                >
                                                    {t('warehouse.modules.receipt.labelPrint.bulkApply', {}, 'Sett alle')}
                                                </button>
                                            </div>
                                            {/* Rad 2: kopier linjeantall til alle + skriv ut alle (antall styres per rad) */}
                                            <div className="flex items-center gap-1">
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); applyLineQtyAll(); }}
                                                    className="flex items-center gap-0.5 px-1.5 py-1 text-[10px] font-medium normal-case bg-white text-blue-700 border border-blue-300 rounded hover:bg-blue-50 min-h-[28px]"
                                                    title={t('warehouse.modules.receipt.labelPrint.useLineQtyAllTooltip', {}, 'Kopier hver linjes eget mottatte antall inn i etikettfeltene')}
                                                >
                                                    <ArrowDownToLine size={11} />
                                                    {t('warehouse.modules.receipt.labelPrint.useLineQtyAll', {}, 'Linjeantall')}
                                                </button>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handlePrintAllLabels(); }}
                                                    disabled={printingAllLabels || !activeReceiptRows.some(({ trans }) => parseInt(labelQty[`${trans.id}`] || '', 10) > 0)}
                                                    className="flex items-center gap-0.5 px-1.5 py-1 text-[10px] font-medium normal-case bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-300 min-h-[28px]"
                                                    title={t('warehouse.modules.receipt.labelPrint.printAllTooltip', {}, 'Skriv ut alle linjer med etikettantall — antallet styres av feltet på hver linje')}
                                                >
                                                    {printingAllLabels
                                                        ? <RefreshCw size={11} className="animate-spin" />
                                                        : <Printer size={11} />}
                                                    {t('warehouse.modules.receipt.labelPrint.printAll', { '0': `${activeReceiptRows.filter(({ trans }) => parseInt(labelQty[`${trans.id}`] || '', 10) > 0).length}` }, `Alle (${activeReceiptRows.filter(({ trans }) => parseInt(labelQty[`${trans.id}`] || '', 10) > 0).length})`)}
                                                </button>
                                            </div>
                                        </div>
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-blue-800 uppercase tracking-wider">{t('warehouse.general.itemNumber', {}, 'Varenr')}</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-blue-800 uppercase tracking-wider">{t('common.fields.description', {}, 'Beskrivelse')}</th>
                                    {receivingMode !== 'order' && (
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-blue-800 uppercase tracking-wider">{t('warehouse.modules.receipt.fields.purchaseOrder', {}, 'Innkjøpsordre')}</th>
                                    )}
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-blue-800 uppercase tracking-wider">{t('warehouse.modules.receipt.fields.ordered', {}, 'Bestilt')}</th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-blue-800 uppercase tracking-wider">{t('warehouse.modules.receipt.fields.arrived', {}, 'Ankommet')}</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-blue-800 uppercase tracking-wider">{t('warehouse.modules.receipt.fields.arrivalLocation', {}, 'Ankomstlokasjon')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {/* Active (unlocked) lines */}
                                {(() => {
                                    return activeReceiptRows.map((row, index) => {
                                        const { receivingLine, trans } = row;

                                        // Initialize batch edit state
                                        initializeBatchEdit(trans);
                                        const transId = `${trans.id}`;
                                        const cachedEditState = batchEditState[transId];

                                        // Get arrived quantity (transaction quantity)
                                        const arrivedQty = cachedEditState?.isEditingQuantity ? cachedEditState.quantity : (trans.quantity || 0);

                                        // Get ordered quantity
                                        const orderedQty = receivingLine.orderLine.quantity || 0;

                                        // Alternating row colors - index 0 = white, index 1 = gray, etc.
                                        const bgColor = index % 2 === 0 ? 'bg-white' : 'bg-gray-50';

                                        return (
                                            <tr
                                                key={`${receivingLine.orderLine.orderNo}-${receivingLine.orderLine.lineNo}-${trans.id}`}
                                                className={`${bgColor} hover:bg-blue-50 cursor-pointer transition-colors`}
                                                onClick={() => handleStcTransClick(trans, receivingLine.orderLine)}
                                            >
                                                {/* 36333950: antall + printerikon — utskrift med operatørens default-printer */}
                                                <td className="px-2 py-2 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                                    <div className="flex items-center gap-1">
                                                        <NumericInput
                                                            mode="integer"
                                                            value={labelQty[transId] ?? ''}
                                                            onChange={(e) => setLabelQty(prev => ({ ...prev, [transId]: e.target.value }))}
                                                            placeholder="0"
                                                            className="w-14 px-1.5 py-1.5 text-sm border border-gray-300 rounded text-right"
                                                        />
                                                        <button
                                                            onClick={() => applyLineQtyToRow(transId, trans.quantity)}
                                                            disabled={!(trans.quantity && trans.quantity > 0)}
                                                            className="p-2 rounded min-h-[36px] min-w-[36px] flex items-center justify-center bg-white text-blue-600 border border-gray-300 hover:bg-blue-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                                            title={t('warehouse.modules.receipt.labelPrint.useLineQtyTooltip', { '0': `${trans.quantity ?? 0}` }, `Bruk mottatt antall (${trans.quantity ?? 0})`)}
                                                        >
                                                            <ArrowDownToLine size={14} />
                                                        </button>
                                                        <button
                                                            onClick={() => handleInlineLabelPrint(trans, receivingLine.orderLine)}
                                                            disabled={printingLabelIds.has(transId) || !(parseInt(labelQty[transId] || '', 10) > 0)}
                                                            className={`p-2 rounded min-h-[36px] min-w-[36px] flex items-center justify-center transition-colors ${
                                                                labelPrintedIds.has(transId)
                                                                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                            } disabled:opacity-40 disabled:cursor-not-allowed`}
                                                            title={t('warehouse.modules.receipt.labelPrint.printTooltip', {}, 'Skriv ut etiketter på din standardprinter')}
                                                        >
                                                            {printingLabelIds.has(transId)
                                                                ? <RefreshCw size={15} className="animate-spin" />
                                                                : <Printer size={15} />}
                                                        </button>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">
                                                    {receivingLine.orderLine.itemNo}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-700">
                                                    <div>{receivingLine.orderLine.description1 || t('common.noDescription', {}, 'Ingen beskrivelse')}</div>
                                                    {trans.itemSerialNo && (
                                                        <div className="text-xs font-mono text-purple-600 mt-0.5">SN: {trans.itemSerialNo}</div>
                                                    )}
                                                    {/* Profil-konfigurert tilleggsinfo fra innkjøpsordrelinjen */}
                                                    <ReceiptExtraInfo
                                                        values={resolveReceiptExtraInfoValues(receivingLine.orderLine, extraInfoFields)}
                                                        compact
                                                        className="mt-0.5"
                                                    />
                                                </td>
                                                {receivingMode !== 'order' && (
                                                    <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                                                        {trans.orderNoERP || trans.orderNo || receivingLine.orderLine.orderNoERP || receivingLine.orderLine.orderNo || '-'}
                                                    </td>
                                                )}
                                                <td className="px-4 py-3 text-sm text-right font-medium text-gray-900 whitespace-nowrap">
                                                    {orderedQty} <span className="font-normal text-gray-500">{receivingLine.orderLine.itemUnitTxt}</span>
                                                </td>
                                                <td className="px-4 py-3 text-sm text-right font-medium text-blue-600 whitespace-nowrap">
                                                    {arrivedQty} <span className="font-normal text-gray-500">{receivingLine.orderLine.itemUnitTxt}</span>
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                                                    {trans.locationName || '-'}
                                                </td>
                                            </tr>
                                        );
                                    });
                                })()}
                            </tbody>
                        </table>
                    </div>

                    {/* Locked lines panel (collapsed by default). Mode-aware: ordre-mottak = fullt levert
                        (quantityDelivered >= bestilt); fritt mottak/bilmottak = virtuelle linjer → fullførte transaksjoner. */}
                    {completedReceiptLines.length > 0 && (
                        <div className="border-t border-gray-200">
                            <button
                                onClick={() => setShowLockedLines(!showLockedLines)}
                                className="flex items-center justify-between w-full px-4 py-3 bg-gray-100 hover:bg-gray-200 transition-colors"
                            >
                                <div className="flex items-center space-x-2">
                                    <Package className="h-5 w-5 text-gray-600" />
                                    <span className="font-medium text-gray-700">
                                        {t('warehouse.modules.receipt.labels.completedReceipts', { count: completedReceiptLines.length }, `Fullførte mottak (${completedReceiptLines.length})`)}
                                    </span>
                                </div>
                                {showLockedLines ? (
                                    <ChevronDown className="h-5 w-5 text-gray-600" />
                                ) : (
                                    <ChevronRight className="h-5 w-5 text-gray-600" />
                                )}
                            </button>

                            {showLockedLines && (
                                <>
                                    {/* Kortvisning under `lg` — samme prinsipp som for de ankomstregistrerte
                                        linjene, men med «Mottatt / Bestilt» og grønn badge. */}
                                    <div className="lg:hidden">
                                        <div className="flex items-center justify-end px-3 py-1.5 bg-blue-100 border-b border-blue-200">
                                            <span className="text-[10px] font-medium text-blue-800 uppercase tracking-wider opacity-75">
                                                {t('warehouse.modules.receipt.fields.received', {}, 'Mottatt')} / {t('warehouse.modules.receipt.fields.ordered', {}, 'Bestilt')}
                                            </span>
                                        </div>
                                        <div className="p-3 space-y-2 bg-gray-50">
                                            {completedReceiptLines.map(line => (
                                                <ReceiptLineCard
                                                    key={`card-${line.key}`}
                                                    itemNo={line.orderLine.itemNo}
                                                    description={line.orderLine.description1 || t('common.noDescription', {}, 'Ingen beskrivelse')}
                                                    quantity={completedDeliveredQty(line)}
                                                    orderedQuantity={line.orderLine.quantity || 0}
                                                    unit={line.orderLine.itemUnitTxt}
                                                    orderNo={receivingMode !== 'order' ? line.displayOrderNo : null}
                                                    variant="completed"
                                                />
                                            ))}
                                        </div>
                                    </div>

                                    <div className="hidden lg:block">
                                    <table className="min-w-full">
                                        {/* Header for locked lines */}
                                        <thead className="bg-blue-100">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-blue-800 uppercase tracking-wider">{t('warehouse.general.itemNumber', {}, 'Varenr')}</th>
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-blue-800 uppercase tracking-wider">{t('common.fields.description', {}, 'Beskrivelse')}</th>
                                                {receivingMode !== 'order' && (
                                                    <th className="px-4 py-3 text-left text-xs font-semibold text-blue-800 uppercase tracking-wider">{t('warehouse.modules.receipt.fields.purchaseOrder', {}, 'Innkjøpsordre')}</th>
                                                )}
                                                <th className="px-4 py-3 text-right text-xs font-semibold text-blue-800 uppercase tracking-wider">{t('warehouse.modules.receipt.fields.ordered', {}, 'Bestilt')}</th>
                                                <th className="px-4 py-3 text-right text-xs font-semibold text-blue-800 uppercase tracking-wider">{t('warehouse.modules.receipt.fields.received', {}, 'Mottatt')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {completedReceiptLines.map((line, index) => {
                                                // Alternating row colors
                                                const bgColor = index % 2 === 0 ? 'bg-white' : 'bg-gray-50';

                                                // Get quantities
                                                const orderedQty = line.orderLine.quantity || 0;
                                                const deliveredQty = completedDeliveredQty(line);

                                                return (
                                                    <tr key={line.key} className={bgColor}>
                                                        <td className="px-4 py-3 text-sm font-medium text-gray-700 whitespace-nowrap">
                                                            {line.orderLine.itemNo}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-gray-600">
                                                            {line.orderLine.description1 || t('common.noDescription', {}, 'Ingen beskrivelse')}
                                                        </td>
                                                        {receivingMode !== 'order' && (
                                                            <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                                                                {line.displayOrderNo || '-'}
                                                            </td>
                                                        )}
                                                        <td className="px-4 py-3 text-sm text-right font-medium text-gray-700 whitespace-nowrap">
                                                            {orderedQty} <span className="font-normal text-gray-500">{line.orderLine.itemUnitTxt}</span>
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-right font-medium text-green-600 whitespace-nowrap">
                                                            {deliveredQty} <span className="font-normal text-gray-500">{line.orderLine.itemUnitTxt}</span>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Melding når ingen linjer har StcTrans eller kumulativt levert */}
            {!receivingLines.some(line => line.receivedTransactions.length > 0 || (line.orderLine.quantityDelivered || 0) > 0) && !newReceipt && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <div className="flex items-center space-x-2">
                        <Package className="h-5 w-5 text-blue-500" />
                        <div>
                            <h3 className="text-blue-800 font-medium">{t('warehouse.modules.receipt.messages.noReceivedItemsYet', {}, 'Ingen mottatte varer ennå')}</h3>
                            <p className="text-blue-700 text-sm mt-1">
                                {t('warehouse.modules.receipt.messages.startReceivingInstructions', {}, 'Bruk søkefeltet ovenfor eller "Alle ordrelinjer" for å starte mottaket. Mottatte varer vil vises her med detaljerte StcTrans oppføringer.')}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Handlingslinje nederst under `lg`. De to avslutt-handlingene er målet med skjermen,
                og hører hjemme innen tommelrekkevidde på håndholdte terminaler — ikke sammenklemt
                som navnløse ikoner i toppraden. Sticky mot bunnen av innholdsflaten, med full
                etikett i begge knappene. */}
            {hasTransactions && (
                <div className="lg:hidden sticky bottom-0 z-20 -mx-6 px-6 py-2 bg-white border-t border-gray-200 shadow-lg">
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleFinishReceiving}
                            disabled={!canFinishReceiving || isFinishingReceiving}
                            className={`flex-1 flex items-center justify-center gap-2 min-h-[48px] px-3 py-2 rounded-lg transition-colors text-sm font-medium ${
                                canFinishReceiving && !isFinishingReceiving
                                    ? 'bg-green-500 text-white hover:bg-green-600'
                                    : 'bg-gray-400 text-gray-700 cursor-not-allowed'
                            }`}
                            title={!canFinishReceiving ? t('warehouse.modules.receipt.messages.cannotFinishYet', {}, 'Mottaket kan ikke avsluttes ennå') : t('warehouse.modules.receipt.actions.finishReceiving', {}, 'Avslutt mottak')}
                        >
                            {isFinishingReceiving ? (
                                <RefreshCw className="h-4 w-4 flex-shrink-0 animate-spin" />
                            ) : (
                                <Check className="h-4 w-4 flex-shrink-0" />
                            )}
                            <span className="truncate">{isFinishingReceiving ? t('warehouse.modules.receipt.actions.finishing', {}, 'Avslutter...') : t('warehouse.modules.receipt.actions.finish', {}, 'Avslutt')}</span>
                        </button>

                        <button
                            onClick={handleFinishReceivingAndPutAway}
                            disabled={!canFinishReceiving || isFinishingReceiving}
                            className={`flex-[2] flex items-center justify-center gap-2 min-h-[48px] px-3 py-2 rounded-lg transition-colors text-sm font-medium ${
                                canFinishReceiving && !isFinishingReceiving
                                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                                    : 'bg-gray-400 text-gray-700 cursor-not-allowed'
                            }`}
                            title={!canFinishReceiving ? t('warehouse.modules.receipt.messages.cannotFinishYet', {}, 'Mottaket kan ikke avsluttes ennå') : t('warehouse.modules.receipt.actions.finishAndPutAway', {}, 'Avslutt og gå til plassering')}
                        >
                            {isFinishingReceiving ? (
                                <RefreshCw className="h-4 w-4 flex-shrink-0 animate-spin" />
                            ) : (
                                <ArrowRight className="h-4 w-4 flex-shrink-0" />
                            )}
                            <span className="truncate">{isFinishingReceiving ? t('warehouse.modules.receipt.actions.processing', {}, 'Prosesserer...') : t('warehouse.modules.receipt.actions.receiptAndPutAway', {}, 'Mottak & Plassering')}</span>
                        </button>
                    </div>
                </div>
            )}

            {/* Line browser modal */}
            {showLineBrowser && (
                <POrderLineBrowser
                    orderNo={orderNo !== undefined ? orderNo : (availableOrderLines[0]?.orderNo ?? 0)}
                    orderLines={availableOrderLines}
                    batchId={receivingMode === 'bilmottak' || receivingMode === 'shipment' ? batchId : undefined}
                    onLineSelect={(line) => {
                        // Validate item master data before starting receipt (advanced warehouses only)
                        runMasterDataValidation([line.itemNo], () => {
                            // Clear editing mode - this is selecting a new line
                            setEditingStcTrans(null);
                            setSelectedSearchLine(line);
                            const remainingQty = calculateRemainingQuantity(line, line.lineNo);
                            setNewReceipt({ lineNo: line.lineNo, quantity: remainingQty, orderNo: line.orderNo });
                            setShowLineBrowser(false);
                        });
                    }}
                    onAllLinesSelect={async (lines) => {
                        // Bulk-registrering av alle valgte linjer med bestilt antall
                        setShowLineBrowser(false);

                        // Validate master data for all unique items in the bulk selection first.
                        // Operator must resolve every invalid item before bulk registration starts.
                        runMasterDataValidation(lines.map(l => l.itemNo), () => {
                            startBulkRegistration(lines);
                        });
                    }}
                    onDirectPlacement={(lines) => {
                        // Norlux P19/P20 — legg valgte linjer direkte ut på lokasjon (uten ankomst)
                        setShowLineBrowser(false);
                        setDirectPlacementLines(lines);
                    }}
                    onClose={() => setShowLineBrowser(false)}
                />
            )}

            {/* Norlux P19/P20 — Direkte utlegg fra ordrelinje */}
            {directPlacementLines && directPlacementLines.length > 0 && (
                <DirectPlacementModal
                    orderNo={orderNo !== undefined ? orderNo : (directPlacementLines[0]?.orderNo ?? 0)}
                    lines={directPlacementLines}
                    onClose={() => setDirectPlacementLines(null)}
                    onSuccess={async () => {
                        setDirectPlacementLines(null);
                        await loadOrderData(true);
                    }}
                />
            )}

            {/* Put-Away Modal */}
            {showPutAwayModal && selectedStcTrans && order && (
                <PutAwayModal
                    isOpen={showPutAwayModal}
                    onClose={handleModalClose}
                    stcTrans={selectedStcTrans}
                    companyNo={selectedStcTrans.companyNo || order.companyNo}
                    warehouseID={selectedStcTrans.warehouse || order.warehouse || 'MAIN'}
                    onSuccess={handleModalSuccess}
                />
            )}

            {/* Edit StcTrans Modal */}
            {showEditModal && selectedStcTrans && (
                <StcTransEditModal
                    isOpen={showEditModal}
                    onClose={handleModalClose}
                    stcTrans={selectedStcTrans}
                    onSuccess={handleModalSuccess}
                />
            )}

            {/* Direct Receipt Modal */}
            {showDirectReceiptModal && selectedStcTrans && order && (
                <DirectReceiptModal
                    isOpen={showDirectReceiptModal}
                    onClose={handleDirectReceiptClose}
                    stcTrans={selectedStcTrans}
                    bulkLines={directReceiptBulkLines}
                    companyNo={selectedStcTrans.companyNo || order.companyNo}
                    warehouseID={selectedStcTrans.warehouse || order.warehouse || 'MAIN'}
                    onSuccess={handleModalSuccess}
                />
            )}

            {/* Advanced Item Config Modal */}
            {showAdvancedConfigModal && advancedConfigItemNo && order && (
                <AdvancedItemConfigModal
                    isOpen={showAdvancedConfigModal}
                    onClose={handleCloseAdvancedConfig}
                    companyNo={selectedSearchLine?.companyNo || order.companyNo}
                    itemNo={advancedConfigItemNo}
                    onSave={handleAdvancedConfigSaved}
                    receivingValidation={
                        currentValidation && currentValidation.itemNo === advancedConfigItemNo
                            ? {
                                binGrpNoMissing: currentValidation.binGrpNoMissing,
                                itemBinSizeMissing: currentValidation.itemBinSizeMissing,
                            }
                            : undefined
                    }
                />
            )}

            {/* Over-Receive Confirmation Modal */}
            <OverReceiveConfirmModal
                isOpen={showOverReceiveConfirmModal}
                message={overReceiveModalMessage}
                onConfirm={handleConfirmOverReceive}
                onCancel={handleCancelOverReceive}
            />

            {/* Serial Number Receipt Modal (ItemType=6) */}
            {showSerialNumberModal && serialNumberPendingData && (
                <SerialNumberReceiptModal
                    isOpen={showSerialNumberModal}
                    onClose={() => {
                        setShowSerialNumberModal(false);
                        setSerialNumberPendingData(null);
                    }}
                    onComplete={handleSerialNumberComplete}
                    itemNo={serialNumberPendingData.itemNo}
                    itemDescription={serialNumberPendingData.itemDescription}
                    expectedQuantity={serialNumberPendingData.quantity}
                />
            )}

            {/* Add PO Modal (Bilmottak only) */}
            {showAddPOModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden">
                        <div className="bg-purple-600 text-white px-4 py-3 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Plus className="h-5 w-5" />
                                <span className="font-semibold">{t('warehouse.modules.receipt.detail.addPurchaseOrders', {}, 'Legg til innkjøpsordrer')}</span>
                            </div>
                            <button onClick={() => setShowAddPOModal(false)} className="text-white hover:bg-purple-500 rounded-full p-1">
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                        <div className="p-4 space-y-3">
                            <div className="relative">
                                <input
                                    type="text"
                                    value={addPOSearchTerm}
                                    onChange={(e) => setAddPOSearchTerm(e.target.value)}
                                    placeholder={t('warehouse.modules.receipt.detail.searchOrderSupplierPlaceholder', {}, 'Søk ordrenr, leverandør...')}
                                    className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                />
                                <Package className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            </div>
                            <div className="border border-gray-200 rounded-lg max-h-56 overflow-y-auto">
                                {filteredAddPOOrders.length === 0 ? (
                                    <div className="text-center py-6 text-gray-400 text-sm">
                                        {addPOAvailableOrders.length === 0 ? t('warehouse.modules.receipt.detail.noAvailableOrders', {}, 'Ingen tilgjengelige ordrer') : t('warehouse.modules.receipt.detail.noMatches', {}, 'Ingen treff')}
                                    </div>
                                ) : (
                                    filteredAddPOOrders.map((o) => {
                                        const sel = addPOSelectedNos.has(o.orderNo);
                                        return (
                                            <button
                                                key={o.orderNo}
                                                type="button"
                                                onClick={() => {
                                                    setAddPOSelectedNos(prev => {
                                                        const n = new Set(prev);
                                                        sel ? n.delete(o.orderNo) : n.add(o.orderNo);
                                                        return n;
                                                    });
                                                }}
                                                className={`w-full px-3 py-2 flex items-center gap-3 text-left border-b border-gray-100 last:border-b-0 transition-colors ${sel ? 'bg-purple-50' : 'hover:bg-gray-50'}`}
                                            >
                                                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${sel ? 'bg-purple-600 border-purple-600' : 'border-gray-300'}`}>
                                                    {sel && <Check className="h-3 w-3 text-white" />}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <span className="font-medium text-sm text-gray-900">{o.orderNoERP || `#${o.orderNo}`}</span>
                                                    <span className="block text-xs text-gray-500 truncate">{o.supplierName || o.supplierNo}</span>
                                                </div>
                                            </button>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                        <div className="bg-gray-50 px-4 py-3 flex justify-between items-center">
                            <span className="text-xs text-gray-500">{t('warehouse.modules.receipt.detail.selectedCount', { 0: addPOSelectedNos.size }, `${addPOSelectedNos.size} valgt`)}</span>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setShowAddPOModal(false)}
                                    className="px-3 py-1.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                                >
                                    {t('common.buttons.cancel', {}, 'Avbryt')}
                                </button>
                                <button
                                    onClick={handleConfirmAddPO}
                                    disabled={addPOSelectedNos.size === 0 || addPOLoading}
                                    className="px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                                >
                                    {addPOLoading ? (
                                        <RefreshCw className="h-3 w-3 animate-spin" />
                                    ) : (
                                        <Plus className="h-3 w-3" />
                                    )}
                                    {t('warehouse.modules.receipt.detail.linkOrders', {}, 'Koble ordrer')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Barcode Modal (TW 36320099) — registrer ukjent skannet strekkode på en av ordrens varer */}
            {showAddBarcodeModal && addBarcodeScanData?.itemNo && (
                <AddBarcodeModal
                    isOpen={showAddBarcodeModal}
                    scannedBarcode={addBarcodeScanData.itemNo}
                    lines={availableOrderLines}
                    companyNo={order?.companyNo || availableOrderLines[0]?.companyNo || ''}
                    onClose={() => {
                        setShowAddBarcodeModal(false);
                        setAddBarcodeScanData(null);
                    }}
                    onCreated={() => {
                        // Koden er registrert — re-kjør skannet med samme data; nå resolver den
                        const scanData = addBarcodeScanData;
                        setShowAddBarcodeModal(false);
                        setAddBarcodeScanData(null);
                        if (scanData) {
                            void handleGS1Scan(scanData);
                        }
                    }}
                />
            )}

            {/* Close Sorting Box Modal */}
            {showCloseSortingBoxModal && closingSortingBoxNo && (
                <CloseSortingBoxModal
                    isOpen={showCloseSortingBoxModal}
                    onClose={() => {
                        setShowCloseSortingBoxModal(false);
                        setClosingSortingBoxNo(null);
                        setClosingSortingBoxTypeId('');
                    }}
                    batchId={batchId}
                    stcBoxNo={closingSortingBoxNo}
                    stcBoxTypeID={closingSortingBoxTypeId}
                    description={sortingBoxDefs.find(d => d.stcBoxTypeID === closingSortingBoxTypeId)?.description1 || closingSortingBoxTypeId}
                    linesInBox={receivingLines.reduce((count, rl) =>
                        count + rl.receivedTransactions.filter(t => t.stcBoxNo === closingSortingBoxNo && t.lineStatus === 20).length, 0
                    )}
                    onBoxClosed={handleSortingBoxClosed}
                />
            )}

            {showReportPicker && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
                        <h3 className="text-lg font-semibold mb-4">{t('warehouse.modules.receipt.detail.selectSortingBoxTemplate', {}, 'Velg sorteringskasse-mal')}</h3>
                        <p className="text-sm text-gray-600 mb-3">
                            {t('warehouse.modules.receipt.detail.multipleTemplatesHint', {}, 'Det finnes flere maler (ReportType=70). Velg hvilken som skal brukes for utskriften.')}
                        </p>
                        <select
                            value={reportPickerSelection ?? ''}
                            onChange={(e) => setReportPickerSelection(Number(e.target.value))}
                            className="w-full border border-gray-300 rounded-md p-2 mb-4"
                        >
                            {sortingBoxReports.map(r => (
                                <option key={r.reportID} value={r.reportID}>
                                    {r.reportName}{r.description ? ` — ${r.description}` : ''}
                                </option>
                            ))}
                        </select>
                        <div className="flex justify-end gap-2">
                            <button
                                onClick={() => {
                                    setShowReportPicker(false);
                                    reportPickerResolverRef.current?.(null);
                                    reportPickerResolverRef.current = null;
                                }}
                                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                            >
                                {t('common.buttons.cancel', {}, 'Avbryt')}
                            </button>
                            <button
                                onClick={() => {
                                    const selected = reportPickerSelection;
                                    setShowReportPicker(false);
                                    reportPickerResolverRef.current?.(selected);
                                    reportPickerResolverRef.current = null;
                                }}
                                disabled={reportPickerSelection === null}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                            >
                                {t('warehouse.modules.receipt.detail.useTemplate', {}, 'Bruk mal')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 36333950: fallback/overstyring når direkte-utskrift feiler eller printer mangler */}
            {fallbackPrint && (
                <ReceiptPrintModal
                    isOpen={true}
                    onClose={() => setFallbackPrint(null)}
                    companyNo={fallbackPrint.companyNo}
                    itemNo={fallbackPrint.itemNo}
                    itemDescription={fallbackPrint.description}
                    batch={fallbackPrint.batch}
                    quantity={fallbackPrint.quantity}
                />
            )}

        </div>
    );
};